import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
// Removed: import './App.css'; // We will use Material UI styling
import UploadForm from './components/UploadForm';
import AssetGrid from './components/AssetGrid';
import BulkEditDrawer from './modules/bulkEdit/BulkEditDrawer';
import AssetDetails from './components/AssetDetails';
import AssetDetailFullPage from './pages/AssetDetailFullPage';
import CategoryList from './components/CategoryList';
import AddCategoryForm from './components/AddCategoryForm';
import SearchBar from './components/SearchBar';
import AdvancedSearchBar, { AdvancedSearchParams } from './components/AdvancedSearchBar';
import EditCategoryModal from './components/EditCategoryModal';
import AssetBadgeManager from './components/AssetBadgeManager';
import { IUser, IAsset, ICategory, ICollection, ITemplate, IBadge } from './types/damTypes';
import axiosInstance from './services/axiosInstance'; // NY: Importera axiosInstance
import TopAppBar from './components/TopAppBar';
import UploadModal from './components/UploadModal';
import EnhancedUploadModal from './components/EnhancedUploadModal';
import AddCategoryModal from './components/AddCategoryModal';
import { UploadManagerProvider } from './contexts/UploadManagerContext';
import { Container, CssBaseline, AppBar, Toolbar, Typography, Button, IconButton, Drawer, Checkbox, Box, Alert, CircularProgress, Paper, Dialog, DialogContent, DialogTitle, DialogActions, LinearProgress, useMediaQuery, Chip, Snackbar, Collapse } from '@mui/material';
import CategoryFilter from './components/CategoryFilter';
import CollectionManagementModal from './components/CollectionManagementModal';
import TemplateManagementModal from './components/TemplateManagementModal';
import BulkOperationBar from './components/BulkOperationBar';
import { API_BASE_URL } from './config';
import CollectionSidebar from './components/CollectionSidebar';
import ShareCollectionDialog from './components/ShareCollectionDialog';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage'; // NY: Importera LoginPage
import ResetPasswordPage from './pages/ResetPasswordPage'; // NY: Importera ResetPasswordPage
import CartModal from './components/CartModal';
import PopupNewsModal from './components/PopupNewsModal';
import PopupSettingsModal from './components/PopupSettingsModal';
import { getAuthToken, getMe, logout as authLogout, checkAndRefreshTokenIfNeeded } from './services/authService'; // NY: Importera auth-funktioner
import { getBadges } from './services/badgeService';
import { hasAnyPermission, hasPermission, canAccessProductComparison } from './utils/permissionUtils';
import { canUseValidooExport } from './modules/validooExport';
import BrandGuideLandingPage from './pages/BrandGuide/BrandGuideLandingPage';
import BrandGuideSectionPage from './pages/BrandGuide/BrandGuideSectionPage';
import PptPoliciesPage from './pages/PptPolicies/PptPoliciesPage';
import BriefsModule from './modules/briefs';
import PromptsModule from './modules/prompts';
import AIImageStudioModule from './modules/aiImageStudio';
import RecipeCorrectionModule from './modules/recipeCorrection';
import InhouseModule from './modules/inhouse';
import ListningarModule from './modules/listningar';
import NyhetslanseringarModule from './modules/nyhetslanseringar';
import BarcodeModule from './modules/barcode';
import TrendsModule from './modules/trends';
import PresentationsModule from './features/presentations';
import KnowledgeHubLauncher from './modules/knowledgeHub/KnowledgeHubLauncher';
import { KnowledgeHubAdminAccessProvider } from './modules/knowledgeHub/KnowledgeHubPermissionContext';
import MobileNavDrawer from './mobile/components/MobileNavDrawer';
import TabletCategoryDrawer from './tablet/TabletCategoryDrawer';
import TabletFilterDrawer from './tablet/TabletFilterDrawer';
import { useAuthSessionLifecycle } from './hooks/useAuthSessionLifecycle';
import ProductImageValidationRunDialog from './modules/productImageValidation/ProductImageValidationRunDialog';
import { useProductImageValidationRunner } from './modules/productImageValidation/useProductImageValidationRunner';
import { getDeepLinkedAssetId } from './utils/assetDeepLink';
import { BRAND_GUIDE_LANDING_PATH, isBrandGuideLandingPath } from './utils/brandGuideRoute';
import MaintenanceNotice from './modules/maintenance/MaintenanceNotice';
import { fetchWordPressPublishingSummary } from './modules/wordpressPublishing/service';

// Onboarding
import { OnboardingProvider } from './contexts/OnboardingContext';
import { OnboardingWrapper } from './components/onboarding';

// Icons
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ListIcon from '@mui/icons-material/List';
import PhotoSizeSelectLargeIcon from '@mui/icons-material/PhotoSizeSelectLarge';
import CloseIcon from '@mui/icons-material/Close';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const drawerWidth = 400; // Define width for the detail panel
const KNOWLEDGE_HUB_UI_ENABLED = process.env.REACT_APP_KNOWLEDGE_HUB_UI !== 'false';
const PRESENTATIONS_UI_ENABLED = process.env.REACT_APP_PRESENTATIONS_UI !== 'false';
const NYHETSLANSERINGAR_UI_ENABLED = process.env.REACT_APP_NYHETSLANSERINGAR_UI !== 'false';

type BrandGuideView =
  | { type: 'landing' }
  | { type: 'section'; slug: string };

type UploadBatchCompletedDetail = {
  assetIds?: string[];
  categoryIds?: string[];
};

const getDefaultSearchParams = (): AdvancedSearchParams => ({
  searchTerm: '',
  fileTypes: [],
  fromDate: null,
  toDate: null,
  favorite: null,
  sortBy: 'uploadedAt',
  sortDirection: 'desc',
  searchFields: ['title', 'description', 'tags', 'aiTags', 'aiDescription', 'fileName', 'metadata'],
  searchOperator: 'AND'
});

// Wrapper component to handle reset-password route BEFORE App loads
function AppWrapper() {
  const urlPath = window.location.pathname;
  const urlSearch = window.location.search;
  
  // Om URL innehåller reset-password, visa ResetPasswordPage direkt
  if (urlPath.includes('reset-password') || urlSearch.includes('token=')) {
    console.log('[AppWrapper] Reset password route detected:', { urlPath, urlSearch });
    return (
      <ResetPasswordPage 
        onBackToLogin={() => {
          window.location.href = '/';
        }} 
      />
    );
  }
  
  // Annars, rendera vanliga App
  return <App />;
}

function isCategoryIdUnderRoot(
  categoryId: string,
  rootId: string | null,
  categoryParentMap: Record<string, string | null>,
): boolean {
  if (!rootId || !categoryId) return false;

  let current: string | null | undefined = categoryId;
  while (current) {
    if (current === rootId) return true;
    current = categoryParentMap[current] || null;
  }

  return false;
}

function getTemplateForCategoryRoot(
  templates: ITemplate[],
  rootId: string | null,
  categoryParentMap: Record<string, string | null>,
): ITemplate | null {
  if (!rootId) return null;

  const matchingTemplates = templates
    .filter((template) =>
      (template.categoryIds || []).some((categoryId) =>
        isCategoryIdUnderRoot(String(categoryId || ''), rootId, categoryParentMap),
      ),
    )
    .sort((a, b) => Number(Boolean(b.isDefault)) - Number(Boolean(a.isDefault)));

  return matchingTemplates[0] || null;
}

const FULL_PAGE_ASSET_HISTORY_KEY = 'damFullAssetDetail';

type FullPageAssetRoute = {
  assetId: string;
  hasBrowseContext: boolean;
};

function getFullPageAssetId(pathname?: string | null): string | null {
  try {
    const resolvedPathname = typeof pathname === 'string'
      ? pathname
      : (typeof window !== 'undefined' ? window.location.pathname : '');
    const match = resolvedPathname.match(/^\/asset\/([^/]+)\/detail\/?$/);
    const rawAssetId = match?.[1];
    if (!rawAssetId) return null;

    const assetId = decodeURIComponent(rawAssetId).trim();
    return assetId || null;
  } catch {
    return null;
  }
}

function getFullPageAssetRouteFromLocation(): FullPageAssetRoute | null {
  const assetId = getFullPageAssetId();
  if (!assetId) return null;

  const historyState = typeof window !== 'undefined'
    ? window.history.state?.[FULL_PAGE_ASSET_HISTORY_KEY]
    : null;

  return {
    assetId,
    hasBrowseContext: historyState?.assetId === assetId && historyState?.hasBrowseContext === true,
  };
}

function buildFullPageAssetPath(assetId: string): string {
  return `/asset/${encodeURIComponent(assetId)}/detail`;
}

function App() {
  // Reset-password route hanteras av AppWrapper ovan
  const [bulkDrawerOpen, setBulkDrawerOpen] = useState(false);

  // Listen for bulk-edit open events from the operation bar (skeleton approach without prop drilling)
  useEffect(() => {
    const handler = (e: any) => {
      try {
        if (Array.isArray(e?.detail?.selectedAssetIds)) {
          // Ensure selection matches current selection (best effort)
        }
      } catch {}
      setBulkDrawerOpen(true);
    };
    window.addEventListener('open-bulk-edit-drawer', handler as any);
    return () => window.removeEventListener('open-bulk-edit-drawer', handler as any);
  }, []);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [fullPageAssetRoute, setFullPageAssetRoute] = useState<FullPageAssetRoute | null>(() => getFullPageAssetRouteFromLocation());
  const [selectionAnchorId, setSelectionAnchorId] = useState<string | null>(null);
  
  // Scroll container ref for grid interactions
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const detailOpenRef = useRef(false);
  const lastCenteredMultiSelectIdRef = useRef<string | null>(null);
  const deepLinkedAssetIdRef = useRef<string | null>(getDeepLinkedAssetId());
  const deepLinkHandledRef = useRef(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isEnhancedUploadModalOpen, setIsEnhancedUploadModalOpen] = useState(false);
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
  const [isAddCollectionModalOpen, setIsAddCollectionModalOpen] = useState(false);
  const [isCollectionManagementModalOpen, setIsCollectionManagementModalOpen] = useState(false);
  const [isTemplateManagementModalOpen, setIsTemplateManagementModalOpen] = useState(false);
  const [isAssetBadgeManagerOpen, setIsAssetBadgeManagerOpen] = useState(false);
  const [assetForBadgeManagement, setAssetForBadgeManagement] = useState<IAsset | null>(null);
  const [badgeManagerAssetIds, setBadgeManagerAssetIds] = useState<string[] | null>(null);
  const [regeneratingThumbnails, setRegeneratingThumbnails] = useState(false);
  const [regenerateMessage, setRegenerateMessage] = useState<string | null>(null);
  const [uploadToast, setUploadToast] = useState<{ open: boolean; message: string; severity: 'info' | 'success' | 'warning' | 'error' }>({
    open: false,
    message: '',
    severity: 'info'
  });
  const {
    dialogOpen: uploadValidationDialogOpen,
    closeDialog: closeUploadValidationDialog,
    job: uploadValidationJob,
    run: uploadValidationRun,
    error: uploadValidationError,
    starting: uploadValidationStarting,
    loadingRun: uploadValidationLoadingRun,
    downloadRun: downloadUploadValidationRun,
    startRun: startUploadValidationRun,
  } = useProductImageValidationRunner();
  // Progress dialog for regenerate-all thumbnails
  const [regenAllDialogOpen, setRegenAllDialogOpen] = useState(false);
  const [regenAllProgress, setRegenAllProgress] = useState<{ runId: string; current: number; total: number; success: number; errors: number; fileName?: string; etaSeconds?: number } | null>(null);
  const regenAllEsRef = useRef<EventSource | null>(null);
  const regenAllRunIdRef = useRef<string>('');
  const regenAllPollIntervalRef = useRef<any>(null);
  
  // Dabas sync-all state
  const [dabasSyncDialogOpen, setDabasSyncDialogOpen] = useState(false);
  const [dabasSyncing, setDabasSyncing] = useState(false);
  const [dabasSyncResults, setDabasSyncResults] = useState<{
    totalAssets: number;
    successCount: number;
    failedCount: number;
    successes: { assetId: string; articleNumber?: string; productName?: string }[];
    failures: { assetId: string; articleNumber?: string; productName?: string; message: string }[];
  } | null>(null);
  const [showDabasSuccessList, setShowDabasSuccessList] = useState(false);
  
  // Thumbnail size state - default to slider midpoint (210px)
  const [thumbnailSize, setThumbnailSize] = useState(210);
  
  // View mode state - default to grid
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // State for Categories
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [categoryToEdit, setCategoryToEdit] = useState<ICategory | null>(null);
  const matCategoryId = useMemo(() => {
    const matCategory = categories.find(cat => (cat.name || '').toLowerCase() === 'mat');
    return matCategory ? matCategory._id : null;
  }, [categories]);
  // State for Collections
  const [collections, setCollections] = useState<ICollection[]>([]);
  const [loadingCollections, setLoadingCollections] = useState(true);
  const [collectionError, setCollectionError] = useState<Error | null>(null);
  // Admin/support view: include ALL users' collections (including private ones)
  const [adminShowAllCollections, setAdminShowAllCollections] = useState(false);

  // State for Templates
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  // State for Advanced Search
  const [searchParams, setSearchParams] = useState<AdvancedSearchParams>(getDefaultSearchParams());
  const [searchResetNonce, setSearchResetNonce] = useState(0);

  // Chain (kedja) filter based on template metadata field
  const [chainFilterKey, setChainFilterKey] = useState<string | null>(null);
  const [wineFilterKey, setWineFilterKey] = useState<string | null>(null);
  // Server-side kontor status filter for Produkter/Vin browse lists
  const [kontorFilter, setKontorFilter] = useState<'all' | 'true' | 'false'>('all');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  const validChainFilterKeys = useMemo(() => {
    const keys = new Set<string>(['__FINNISH_PRODUCTS__']);
    const productsTemplate = templates.find(
      (template) => String(template?.name || '').trim().toLowerCase() === 'produkter',
    );

    (productsTemplate?.fields || []).forEach((field) => {
      if (!field || field.type === 'separator' || field.showInChainDropdown !== true) {
        return;
      }

      const key = String(field.id || field.name || '').trim();
      if (key) {
        keys.add(key);
      }
    });

    return keys;
  }, [templates]);

  // State for Category Filtering
  const [selectedFilterCategoryIds, setSelectedFilterCategoryIds] = useState<string[]>([]);

  // State for Badge Filtering
  const [selectedFilterBadgeIds, setSelectedFilterBadgeIds] = useState<string[]>([]);
  const [badgeFilterMode, setBadgeFilterMode] = useState<'AND' | 'OR'>('AND');
  // All configured badges, fetched independently so the filter is not limited to loaded assets
  const [allBadges, setAllBadges] = useState<IBadge[]>([]);
  // State for Tag Filtering
  const [selectedFilterTagNames, setSelectedFilterTagNames] = useState<string[]>([]);
  const [selectedFilterAiTagNames, setSelectedFilterAiTagNames] = useState<string[]>([]);
  const [popularAiTags, setPopularAiTags] = useState<{ tag: string; count: number }[]>([]);
  const [popularAiTagsLoading, setPopularAiTagsLoading] = useState(false);
  const categoryParentMap = useMemo(() => {
    const map: Record<string, string | null> = {};
    categories.forEach(cat => {
      if (typeof cat.parentCategory === 'string') {
        map[cat._id] = cat.parentCategory;
      } else if (cat.parentCategory && typeof cat.parentCategory === 'object') {
        map[cat._id] = (cat.parentCategory as ICategory)._id || null;
      } else {
        map[cat._id] = null;
      }
    });
    return map;
  }, [categories]);

  const productsRootId = useMemo(() => {
    const exactRoot = categories.find(
      (category) => (category.name || '').trim().toLowerCase() === 'produkter' && !category.parentCategory,
    );
    const fallback = categories.find(
      (category) => (category.name || '').trim().toLowerCase() === 'produkter',
    );
    return exactRoot?._id || fallback?._id || null;
  }, [categories]);

  const wineRootId = useMemo(() => {
    const exactRoot = categories.find(
      (category) => (category.name || '').trim().toLowerCase() === 'vin' && !category.parentCategory,
    );
    const fallback = categories.find(
      (category) => (category.name || '').trim().toLowerCase() === 'vin',
    );
    return exactRoot?._id || fallback?._id || null;
  }, [categories]);

  const isUnderProductsFilterContext = useMemo(() => {
    if (!productsRootId || selectedFilterCategoryIds.length === 0) return false;
    return selectedFilterCategoryIds.some((categoryId) =>
      isCategoryIdUnderRoot(categoryId, productsRootId, categoryParentMap),
    );
  }, [selectedFilterCategoryIds, productsRootId, categoryParentMap]);

  const isUnderWineFilterContext = useMemo(() => {
    if (!wineRootId || selectedFilterCategoryIds.length === 0) return false;
    return selectedFilterCategoryIds.some((categoryId) =>
      isCategoryIdUnderRoot(categoryId, wineRootId, categoryParentMap),
    );
  }, [selectedFilterCategoryIds, wineRootId, categoryParentMap]);

  const wineTemplate = useMemo(
    () => getTemplateForCategoryRoot(templates, wineRootId, categoryParentMap),
    [templates, wineRootId, categoryParentMap],
  );

  const validWineFilterKeys = useMemo(() => {
    const keys = new Set<string>();

    (wineTemplate?.fields || []).forEach((field) => {
      if (!field || field.type === 'separator' || field.showInWineDropdown !== true) {
        return;
      }

      const key = String(field.id || field.name || '').trim();
      if (key) {
        keys.add(key);
      }
    });

    return keys;
  }, [wineTemplate]);

  const matRelatedCategoryId = useMemo(() => {
    if (!matCategoryId) return null;
    const relevantIds = selectedFilterCategoryIds.filter(id => {
      if (id === matCategoryId) return true;
      let current: string | null = id;
      const visited = new Set<string>();
      while (current) {
        if (current === matCategoryId) return true;
        if (visited.has(current)) break;
        visited.add(current);
        current = current ? (categoryParentMap[current] ?? null) : null;
      }
      return false;
    });
    if (relevantIds.length === 0) return null;
    return relevantIds[relevantIds.length - 1];
  }, [matCategoryId, selectedFilterCategoryIds, categoryParentMap]);
  const productCategoryIds = useMemo(() => {
    const rootsPrefer = categories
      .filter(cat => (cat.name || '').trim().toLowerCase() === 'produkter' && !categoryParentMap[cat._id])
      .map(cat => cat._id);
    const rootsAny = categories
      .filter(cat => (cat.name || '').trim().toLowerCase() === 'produkter')
      .map(cat => cat._id);
    const roots = new Set(rootsPrefer.length > 0 ? rootsPrefer : rootsAny);
    const ids = new Set<string>();

    categories.forEach(cat => {
      let current: string | null = cat._id;
      const visited = new Set<string>();
      while (current) {
        if (roots.has(current)) {
          ids.add(cat._id);
          break;
        }
        if (visited.has(current)) break;
        visited.add(current);
        current = current ? (categoryParentMap[current] ?? null) : null;
      }
    });

    return ids;
  }, [categories, categoryParentMap]);
  const wineCategoryIds = useMemo(() => {
    const rootsPrefer = categories
      .filter(cat => (cat.name || '').trim().toLowerCase() === 'vin' && !categoryParentMap[cat._id])
      .map(cat => cat._id);
    const rootsAny = categories
      .filter(cat => (cat.name || '').trim().toLowerCase() === 'vin')
      .map(cat => cat._id);
    const roots = new Set(rootsPrefer.length > 0 ? rootsPrefer : rootsAny);
    const ids = new Set<string>();

    categories.forEach(cat => {
      let current: string | null = cat._id;
      const visited = new Set<string>();
      while (current) {
        if (roots.has(current)) {
          ids.add(cat._id);
          break;
        }
        if (visited.has(current)) break;
        visited.add(current);
        current = current ? (categoryParentMap[current] ?? null) : null;
      }
    });

    return ids;
  }, [categories, categoryParentMap]);
  // State for Assets
  const [assets, setAssets] = useState<IAsset[]>([]);
  const [totalAssetCount, setTotalAssetCount] = useState<number | undefined>(undefined);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [assetError, setAssetError] = useState<Error | null>(null);

  // Multi-select state
  const [multiSelectEnabled, setMultiSelectEnabled] = useState(false);
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const manualMultiSelectEnabledRef = useRef(false);

  // Shopping cart state
  const [cartItems, setCartItems] = useState<{[assetId: string]: number}>({});
  const [cartAssets, setCartAssets] = useState<IAsset[]>([]);
  const [showCartModal, setShowCartModal] = useState(false);

  // New state for collection sidebar - always show but default to collapsed
  const [showCollectionSidebar, setShowCollectionSidebar] = useState(true);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);

  // New state for ShareCollectionDialog
  const [shareCollectionDialogOpen, setShareCollectionDialogOpen] = useState(false);
  const [collectionToShare, setCollectionToShare] = useState<ICollection | null>(null);
  const [collectionToDelete, setCollectionToDelete] = useState<ICollection | null>(null);

  // New state for CollectionDetailView
  const [showCollectionDetail, setShowCollectionDetail] = useState(false);
  // Auto-activate multi-select UI when 2+ assets are selected; close asset view when multi
  React.useEffect(() => {
    try {
      if (selectedAssetIds.length >= 2) {
        if (!multiSelectEnabled) setMultiSelectEnabled(true);
        // Close any open asset view when entering multi
        // Some code paths use selectedAssetId or showCollectionDetail; clear asset detail selection
        try { setSelectedAssetId && setSelectedAssetId(null as any); } catch {}
      } else if (selectedAssetIds.length <= 1 && !manualMultiSelectEnabledRef.current) {
        // Auto-enabled multi-select should fall back when the selection returns to one item.
        if (multiSelectEnabled) setMultiSelectEnabled(false);
      }
    } catch {}
  }, [selectedAssetIds, multiSelectEnabled]);

  // NYTT: State för att hantera vilken inställningsvy som visas
  type SettingsView =
    | 'roles'
    | 'users'
    | 'apikeys'
    | 'backups'
    | 'inventory'
    | 'csv'
    | 'pptx-template-field-import'
    | 'field-diagnostics'
    | 'favicon'
    | 'archive'
    | 'trash'
    | 'duplicates'
    | 'download-templates'
    | 'pdf-templates'
    | 'thumbnails'
    | 'categories'
    | 'badges'
    | 'support-page'
    | 'login-page'
    | 'ai-queue'
    | 'popup'
    | 'brand-guide'
    | 'ppt-policies'
    | 'analytics'
    | 'maintenance'
    | 'imagemagick-test'
    | 'issues-tasks'
    | 'dabas-sync'
    | 'product-comparison'
    | 'product-image-validation'
    | 'dev-snapshots'
    | 'knowledge-hub'
    | 'wordpress-publishing'
    | null;

  const settingsViewOrder = useMemo<Exclude<SettingsView, null>[]>(() => [
    'roles',
    'users',
    'apikeys',
    'backups',
    'inventory',
    'csv',
    'pptx-template-field-import',
    'field-diagnostics',
    'favicon',
    'archive',
    'trash',
    'duplicates',
    'download-templates',
    'pdf-templates',
    'thumbnails',
    'categories',
    'badges',
    'support-page',
    'login-page',
    'ai-queue',
    'popup',
    'brand-guide',
    'ppt-policies',
    'analytics',
    'maintenance',
    'imagemagick-test',
    'issues-tasks',
    'dabas-sync',
    'product-comparison',
    'product-image-validation',
    'dev-snapshots',
    'knowledge-hub',
    'wordpress-publishing',
  ], []);
  const [currentSettingsView, setCurrentSettingsView] = useState<SettingsView>(null);

  const [currentUser, setCurrentUser] = useState<IUser | null>(null);
  const [authLoading, setAuthLoading] = useState<boolean>(true);
  const [wordpressPendingAssetCount, setWordpressPendingAssetCount] = useState(0);
  const refreshWordPressPendingAssetCount = useCallback(async () => {
    if (!hasPermission(currentUser, 'admin:all')) {
      setWordpressPendingAssetCount(0);
      return;
    }

    try {
      const summary = await fetchWordPressPublishingSummary();
      setWordpressPendingAssetCount(Math.max(0, Number(summary.pendingAssetCount || 0)));
    } catch (error) {
      console.warn('Unable to refresh WordPress pending asset count:', error);
      setWordpressPendingAssetCount(0);
    }
  }, [currentUser]);
  const canRunProductImageValidation = hasPermission(currentUser, 'system:runProductImageValidation');
  const maybeRunProductImageValidationForUpload = useCallback((detail?: UploadBatchCompletedDetail) => {
    const detailAssetIds = detail?.assetIds;
    const detailCategoryIds = detail?.categoryIds;
    const rawAssetIds = Array.isArray(detailAssetIds) ? detailAssetIds : [];
    const rawCategoryIds = Array.isArray(detailCategoryIds) ? detailCategoryIds : [];
    const assetIds = Array.from(new Set(rawAssetIds.map((id) => String(id)).filter(Boolean)));
    const categoryIds = Array.from(new Set(rawCategoryIds.map((id) => String(id)).filter(Boolean)));

    if (assetIds.length === 0 || categoryIds.length === 0) {
      return;
    }

    const isUnderProducts = categoryIds.some((categoryId) => productCategoryIds.has(categoryId));
    const isUnderWineOnly = !isUnderProducts && categoryIds.some((categoryId) => wineCategoryIds.has(categoryId));
    if (!isUnderProducts && !isUnderWineOnly) {
      return;
    }

    if (!canRunProductImageValidation) {
      setUploadToast({
        open: true,
        message: 'Bildkontroll hoppades över pga. behörighet',
        severity: 'warning',
      });
      return;
    }

    void startUploadValidationRun('selected', assetIds);
  }, [canRunProductImageValidation, productCategoryIds, wineCategoryIds, startUploadValidationRun]);

  // Ensure the admin-only toggle never stays enabled for non-admins (or after logout)
  useEffect(() => {
    if (!currentUser || !hasPermission(currentUser, 'admin:all')) {
      if (adminShowAllCollections) setAdminShowAllCollections(false);
    }
  }, [currentUser, adminShowAllCollections]);

  useEffect(() => {
    void refreshWordPressPendingAssetCount();
  }, [refreshWordPressPendingAssetCount]);

  // Popup news modal state
  const [showPopupNews, setShowPopupNews] = useState(false);
  const [showPopupSettings, setShowPopupSettings] = useState(false);
  const [brandGuideView, setBrandGuideView] = useState<BrandGuideView | null>(() => (
    isBrandGuideLandingPath() ? { type: 'landing' } : null
  ));
  const [brandGuideEditorSlug, setBrandGuideEditorSlug] = useState<string | null>(null);
  const [showPptPolicies, setShowPptPolicies] = useState(false);
  const [showBriefs, setShowBriefs] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);
  const [showAiImageStudio, setShowAiImageStudio] = useState(false);
  const [initialAiRecipeAssetId, setInitialAiRecipeAssetId] = useState<string | null>(null);
  const [initialAiEditAssetId, setInitialAiEditAssetId] = useState<string | null>(null);
  const [showRecipeCorrection, setShowRecipeCorrection] = useState(false);
  const [recipeCorrectionAssetId, setRecipeCorrectionAssetId] = useState<string | null>(null);
  const [recipeCorrectionOpenedFromFullPage, setRecipeCorrectionOpenedFromFullPage] = useState(false);
  const [recipeCorrectionSnapshotMode, setRecipeCorrectionSnapshotMode] = useState(false);
  const [recipeSavedNotice, setRecipeSavedNotice] = useState(false);
  const [fullPageRemountKey, setFullPageRemountKey] = useState(0);
  const [showInhouse, setShowInhouse] = useState(false);
  const [showListningar, setShowListningar] = useState(false);
  const [showNyhetslanseringar, setShowNyhetslanseringar] = useState(false);
  const [showBarcodeGenerator, setShowBarcodeGenerator] = useState(false);
  const [showTrends, setShowTrends] = useState(false);
  const [showPresentations, setShowPresentations] = useState(false);

  // Mobile detection for responsive UI adjustments
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(min-width:601px) and (max-width:1199px)');
  const isLargeTouchViewport = useMediaQuery('(pointer: coarse) and (min-width:1200px) and (max-width:1500px)');
  const [hasMultipleTouchPoints, setHasMultipleTouchPoints] = useState(false);
  useEffect(() => {
    setHasMultipleTouchPoints(typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1);
  }, []);

  // Landscape mobile: phone in landscape (wide but short screen, NOT tablet/iPad)
  const isLandscapeMobile = useMediaQuery('(min-width:601px) and (max-height:500px)');
  // Allow user to disable compact landscape mode via localStorage
  const [landscapeCompactDisabled, setLandscapeCompactDisabled] = useState(() => {
    try { return localStorage.getItem('landscapeCompactDisabled') === 'true'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('landscapeCompactDisabled', String(landscapeCompactDisabled)); } catch {}
  }, [landscapeCompactDisabled]);
  const showCompactLandscape = isLandscapeMobile && !landscapeCompactDisabled;
  const isBrowseView = !brandGuideView && !showPptPolicies && !showBriefs && !showPrompts && !showAiImageStudio && !showRecipeCorrection && !showInhouse && !showListningar && !showNyhetslanseringar && !showBarcodeGenerator && !showTrends && !showPresentations && !currentSettingsView;
  // Captures iPad Pro landscape without broadening tablet mode to desktop widths.
  const isLargeTouchTablet = isLargeTouchViewport && hasMultipleTouchPoints;
  const isTabletShellMode = (isTablet || isLargeTouchTablet) && !showCompactLandscape;
  const isTabletBrowseMode = isTabletShellMode && isBrowseView;
  const [bulkOperationBarHeight, setBulkOperationBarHeight] = useState(0);
  const bulkOperationBarVisible = (selectedAssetIds.length >= 2 || multiSelectEnabled) && selectedAssetIds.length > 0;
  const bulkOperationBarInsetPx = bulkOperationBarVisible ? Math.max(bulkOperationBarHeight, 64) : 0;

  // Category drawer for landscape mobile (reuse MobileCategoryDrawer concept)
  const [landscapeCatDrawerOpen, setLandscapeCatDrawerOpen] = useState(false);
  const [tabletCategoryDrawerOpen, setTabletCategoryDrawerOpen] = useState(false);
  const [tabletFiltersOpen, setTabletFiltersOpen] = useState(false);

  // Auto-set smallest thumbnail size in landscape mobile
  const prevCompactRef = useRef(false);
  useEffect(() => {
    if (showCompactLandscape && !prevCompactRef.current) {
      setThumbnailSize(140); // PRESETS.MIN.thumb
    }
    prevCompactRef.current = showCompactLandscape;
  }, [showCompactLandscape]);

  // Function to toggle the collection sidebar
  const toggleCollectionSidebar = () => {
    setShowCollectionSidebar(prev => !prev);
  };

  // Function to toggle the collection sidebar collapse state - default to collapsed
  const [collectionSidebarCollapsed, setCollectionSidebarCollapsed] = useState(true);
  const toggleCollectionSidebarCollapsed = () => {
    setCollectionSidebarCollapsed(prev => !prev);
  };

  // Asset fetch coordination: latest replace request wins, while append requests stay deduplicated.
  const activeAssetsAbortControllerRef = useRef<AbortController | null>(null);
  const appendAssetsInFlightRef = useRef<Promise<void> | null>(null);
  const latestAssetsRequestIdRef = useRef(0);
  const ASSETS_PAGE_SIZE = 300;
  type FetchAssetsMode = 'replace' | 'append';
  type FetchAssetsOptions = {
    mode?: FetchAssetsMode;
  };
  // Keep latest fetchAssets in a ref so one-time event listeners don't capture a stale closure
  const fetchAssetsLatestRef = useRef<(options?: FetchAssetsOptions) => Promise<void>>(async () => {});
  const lastUploadValidationRunIdRef = useRef<string | null>(null);
  const previousCategoryFilterKeyRef = useRef<string | null>(null);

  // Function to fetch assets
  const fetchAssets = async (options: FetchAssetsOptions = {}): Promise<void> => {
    const { mode = 'replace' } = options;
    const isAppend = mode === 'append';

    if (isAppend && totalAssetCount !== undefined && assets.length >= totalAssetCount) {
      return;
    }

    if (isAppend) {
      if (appendAssetsInFlightRef.current) return appendAssetsInFlightRef.current;
      if (activeAssetsAbortControllerRef.current) return;
    }

    const requestId = latestAssetsRequestIdRef.current + 1;
    latestAssetsRequestIdRef.current = requestId;

    if (!isAppend && activeAssetsAbortControllerRef.current) {
      activeAssetsAbortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    activeAssetsAbortControllerRef.current = abortController;

    const requestPromise = (async () => {
      try {
        // Om ingen användare är inloggad, skippa hämtning av assets
        if (!currentUser) {
          console.log('Skippar fetchAssets eftersom ingen användare är inloggad');
          return;
        }
        
        const loadedCountBeforeFetch = isAppend ? assets.length : 0;
        
        setLoadingAssets(true);
        setAssetError(null);

        // Kontrollera att token finns tillgänglig innan assets hämtas
        const token = getAuthToken();
        if (!token) {
          console.warn('Ingen token tillgänglig vid hämtning av assets');
          setAssetError(new Error('Du måste vara inloggad för att se assets'));
          setLoadingAssets(false);
          
          // Om token saknas men användare finns, vänta och försök igen
          if (currentUser) {
            console.log('Användare finns men token saknas, försöker igen om 500ms');
            setTimeout(() => {
              const retryToken = getAuthToken();
              if (retryToken) {
                console.log('Token hittad vid återförsök, hämtar assets på nytt');
                void fetchAssetsLatestRef.current(options);
              }
            }, 500);
          }
          return;
        }

        // Kontrollera och förnya token om det behövs
        await checkAndRefreshTokenIfNeeded();

        if (abortController.signal.aborted || requestId !== latestAssetsRequestIdRef.current) {
          return;
        }

        const queryParams = new URLSearchParams();
        
        if (searchParams.searchTerm) {
          queryParams.append('search', searchParams.searchTerm);
          if (searchParams.searchOperator) {
            queryParams.append('operator', searchParams.searchOperator);
          }
          
          if (!recentSearches.includes(searchParams.searchTerm) && searchParams.searchTerm.trim() !== '') {
            setRecentSearches(prev => {
              const newSearches = [searchParams.searchTerm, ...prev.slice(0, 9)];
              localStorage.setItem('recentSearches', JSON.stringify(newSearches));
              return newSearches;
            });
          }
        }
        
        if (selectedFilterCategoryIds.length > 0) {
          queryParams.append('category', selectedFilterCategoryIds.join(','));
        }
        
        if (selectedFilterBadgeIds.length > 0) {
          queryParams.append('badges', selectedFilterBadgeIds.join(','));
          queryParams.append('badgeFilterMode', badgeFilterMode);
        }
        if (selectedFilterTagNames.length > 0) {
          queryParams.append('tags', selectedFilterTagNames.join(','));
        }
        if (selectedFilterAiTagNames.length > 0) {
          queryParams.append('aiTags', selectedFilterAiTagNames.join(','));
        }
        
        if (searchParams.collectionId) {
          queryParams.append('collection', searchParams.collectionId);
        }
        
        if (searchParams.fileTypes.length > 0) {
          queryParams.append('fileTypes', searchParams.fileTypes.join(','));
        }
        
        if (searchParams.fromDate) {
          queryParams.append('fromDate', searchParams.fromDate.toISOString());
        }
        if (searchParams.toDate) {
          queryParams.append('toDate', searchParams.toDate.toISOString());
        }
        
        if (searchParams.favorite !== null) {
          queryParams.append('favorite', searchParams.favorite.toString());
        }
        
        if (searchParams.searchFields.length > 0) {
          queryParams.append('searchFields', searchParams.searchFields.join(','));
        }

        // Apply metadata filter from the active Produkter/VIN dropdown
        if (isUnderProductsFilterContext && chainFilterKey && validChainFilterKeys.has(chainFilterKey)) {
          queryParams.append('metaKey', chainFilterKey);
          queryParams.append('metaHasValue', 'true');
        } else if (isUnderWineFilterContext && wineFilterKey && validWineFilterKeys.has(wineFilterKey)) {
          queryParams.append('metaKey', wineFilterKey);
          queryParams.append('metaHasValue', 'true');
          queryParams.append('metaFilterContext', 'wine');
        }

        if (kontorFilter === 'true' || kontorFilter === 'false') {
          queryParams.append('kontor', kontorFilter);
        }
        
        if (searchParams.sortBy) {
          queryParams.append('sortBy', searchParams.sortBy);
          queryParams.append('sortDirection', searchParams.sortDirection);
        }
        queryParams.append('limit', String(ASSETS_PAGE_SIZE));
        if (isAppend && loadedCountBeforeFetch > 0) {
          queryParams.append('skip', String(loadedCountBeforeFetch));
        }
        
        console.log('Fetching assets with params:', queryParams.toString());
        
        // Filtrera bort arkiverade från huvudlistan
        if (!queryParams.has('archived')) {
          queryParams.append('archived', 'false');
        }
        const response = await axiosInstance.get(`/api/assets`, {
          params: Object.fromEntries(queryParams),
          signal: abortController.signal,
        });

        if (requestId !== latestAssetsRequestIdRef.current) {
          return;
        }
        
        console.log('Fetched assets:', response.data.length);
        const serverTotal = response.headers['x-total-count'];
        const parsedTotal = serverTotal ? parseInt(serverTotal, 10) : NaN;
        setTotalAssetCount(Number.isFinite(parsedTotal) ? parsedTotal : undefined);
        const fetchedAssets = Array.isArray(response.data) ? response.data : [];
        if (isAppend) {
          setAssets(prevAssets => {
            if (fetchedAssets.length === 0) return prevAssets;
            const seen = new Set(prevAssets.map(asset => asset._id));
            const merged = [...prevAssets];
            for (const asset of fetchedAssets) {
              if (!seen.has(asset._id)) {
                seen.add(asset._id);
                merged.push(asset);
              }
            }
            return merged;
          });
        } else {
          setAssets(fetchedAssets);
        }
      } catch (err: any) {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
          return;
        }
        if (requestId !== latestAssetsRequestIdRef.current) {
          return;
        }
        setAssetError(new Error(err.response?.data?.message || err.message || 'Error fetching assets'));
        console.error('Error fetching assets:', err.response?.data || err);
      } finally {
        if (isAppend) {
          appendAssetsInFlightRef.current = null;
        }
        if (activeAssetsAbortControllerRef.current === abortController) {
          activeAssetsAbortControllerRef.current = null;
        }
        if (requestId === latestAssetsRequestIdRef.current) {
          setLoadingAssets(false);
        }
      }
    })();

    if (isAppend) {
      appendAssetsInFlightRef.current = requestPromise;
    }

    return requestPromise;
  };
  // Update ref every render to avoid stale closures in event listeners
  fetchAssetsLatestRef.current = fetchAssets;

  useEffect(() => {
    return () => {
      activeAssetsAbortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const completedRunId = uploadValidationRun?.runId;
    if (!completedRunId) return;
    if (lastUploadValidationRunIdRef.current === completedRunId) return;

    lastUploadValidationRunIdRef.current = completedRunId;
    try {
      void fetchAssetsLatestRef.current();
    } catch {}
  }, [uploadValidationRun?.runId]);

  // Function to fetch categories
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      setCategoryError(null);
      const response = await axiosInstance.get<ICategory[]>(`/api/categories/browse`);
      setCategories(response.data);
    } catch (err: any) {
      setCategoryError(err.response?.data?.message || err.message || 'Error fetching categories');
      console.error('Error fetching categories:', err.response?.data || err);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Function to fetch collections
  const fetchCollections = async () => {
    try {
      setLoadingCollections(true);
      setCollectionError(null);
      const isAdmin = !!currentUser && hasPermission(currentUser, 'admin:all');
      const response = await axiosInstance.get<ICollection[]>(
        `/api/collections`,
        isAdmin && adminShowAllCollections ? { params: { includeAll: true } } : undefined
      );
      setCollections(response.data);
    } catch (err: any) {
      setCollectionError(new Error(err.response?.data?.message || err.message || 'Error fetching collections'));
      console.error('Error fetching collections:', err.response?.data || err);
    } finally {
      setLoadingCollections(false);
    }
  };

  // Function to fetch templates
  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      setTemplateError(null);
      const response = await axiosInstance.get<ITemplate[]>(`/api/templates`);
      setTemplates(response.data);
    } catch (err: any) {
      setTemplateError(err.response?.data?.message || err.message || 'Error fetching templates');
      console.error('Error fetching templates:', err.response?.data || err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    if (!chainFilterKey) return;
    if (!validChainFilterKeys.has(chainFilterKey)) {
      setChainFilterKey(null);
    }
  }, [chainFilterKey, validChainFilterKeys]);

  useEffect(() => {
    if (!wineFilterKey) return;
    if (!isUnderWineFilterContext || !validWineFilterKeys.has(wineFilterKey)) {
      setWineFilterKey(null);
    }
  }, [wineFilterKey, isUnderWineFilterContext, validWineFilterKeys]);

  const handleChainFilterChange = useCallback((key: string | null) => {
    setChainFilterKey(key);
    if (key) {
      setWineFilterKey(null);
    }
  }, []);

  const handleWineFilterChange = useCallback((key: string | null) => {
    setWineFilterKey(key);
    if (key) {
      setChainFilterKey(null);
    }
  }, []);

  const handleKontorFilterChange = useCallback((next: 'all' | 'true' | 'false') => {
    setKontorFilter(next);
    setSelectedAssetIds([]);
    setSelectionAnchorId(null);
    setAssets([]);
    setTotalAssetCount(undefined);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, []);

  const canFilterKontorProducts = hasPermission(currentUser, 'assets:filterKontorProducts');
  const canFilterKontorWine = hasPermission(currentUser, 'assets:filterKontorWine');

  useEffect(() => {
    if (kontorFilter === 'all') return;
    const allowedInProducts = canFilterKontorProducts && isUnderProductsFilterContext;
    const allowedInWine = canFilterKontorWine && isUnderWineFilterContext;
    if (!allowedInProducts && !allowedInWine) {
      setKontorFilter('all');
    }
  }, [
    kontorFilter,
    canFilterKontorProducts,
    canFilterKontorWine,
    isUnderProductsFilterContext,
    isUnderWineFilterContext,
  ]);

  const handleAssetGridSortOrderChange = useCallback((sortOrder: string) => {
    setSearchParams(prev => {
      if (sortOrder === 'wp_newest') {
        return { ...prev, sortBy: 'wordpressPublishedAt', sortDirection: 'desc' };
      }
      if (sortOrder === 'wp_oldest') {
        return { ...prev, sortBy: 'wordpressPublishedAt', sortDirection: 'asc' };
      }
      if (sortOrder === 'oldest') {
        return { ...prev, sortBy: 'uploadedAt', sortDirection: 'asc' };
      }
      return { ...prev, sortBy: 'uploadedAt', sortDirection: 'desc' };
    });
  }, []);

  // NYTT: Handlers för att öppna olika inställningsvyer
  const handleOpenRoleSettings = () => {
    setSelectedAssetId(null); // Stäng eventuell asset detailvy
    setShowCollectionDetail(false); // Korrekt: sätt till false
    openSettingsView('roles');
  };

  const handleOpenUserSettings = () => { // För framtida bruk
    setSelectedAssetId(null);
    setShowCollectionDetail(false); // Korrekt: sätt till false
    openSettingsView('users');
  };

  const resetFiltersToAll = () => {
    setSearchParams(getDefaultSearchParams());
    setChainFilterKey(null);
    setWineFilterKey(null);
    setSelectedFilterCategoryIds([]);
    setSelectedFilterBadgeIds([]);
    setSelectedFilterTagNames([]);
    setSelectedFilterAiTagNames([]);
    setBadgeFilterMode('AND');
    setSearchResetNonce(prev => prev + 1);
    setSelectedCollectionId(null);
  };

  const navigateToBrowseView = useCallback(() => {
    setSelectedCollectionId(null);
    setShowCollectionDetail(false);
    setSearchParams(prev => {
      const newParams = { ...prev };
      delete newParams.collectionId;
      return newParams;
    });
    setCurrentSettingsView(null);
    setBrandGuideView(null);
    setBrandGuideEditorSlug(null);
    setShowPptPolicies(false);
    setShowBriefs(false);
    setShowPrompts(false);
    setShowAiImageStudio(false);
    setShowRecipeCorrection(false);
    setShowInhouse(false);
    setShowListningar(false);
    setShowNyhetslanseringar(false);
    setShowBarcodeGenerator(false);
    setShowTrends(false);
    setShowPresentations(false);
    setTabletCategoryDrawerOpen(false);
    setTabletFiltersOpen(false);
    // Rensa hash (#categories etc.) utan omladdning
    try {
      const cleanUrl = window.location.pathname + window.location.search;
      window.history.replaceState(null, '', cleanUrl);
    } catch {}
  }, []);

  // Funktion för att hantera byte från settings/collections tillbaka till huvudvyn
  const handleBackToMainView = () => {
    navigateToBrowseView();

    // Explicit forcera hämtning av assets när användaren återvänder till huvudvyn
    setTimeout(() => {
      try { fetchAssetsLatestRef.current(); } catch {}
    }, 200);
  };

  const handleLogoClick = () => {
    handleBackToMainView();
    resetFiltersToAll();
    // TODO: Om loggan även ska stänga modaler/dialoger, nollställ dem här.
  };

  const handleAssetUpdate = useCallback(() => {
    void fetchAssetsLatestRef.current();
    void refreshWordPressPendingAssetCount();
  }, [refreshWordPressPendingAssetCount]);

  // Hjälpfunktion för att öppna en specifik settings-vy och sätta rätt hash
  const openSettingsView = (view: Exclude<SettingsView, null>) => {
    try {
      window.location.hash = view;
    } catch {}
    setShowCollectionDetail(false);
    setBrandGuideView(null);
    setShowPptPolicies(false);
    setShowBriefs(false);
    setShowPrompts(false);
    setShowAiImageStudio(false);
    setShowRecipeCorrection(false);
    setShowInhouse(false);
    setShowListningar(false);
    setShowNyhetslanseringar(false);
    setShowBarcodeGenerator(false);
    setShowTrends(false);
    setShowPresentations(false);
    setCurrentSettingsView(view);
  };

  const canAccessSettingsView = useCallback((view: Exclude<SettingsView, null>): boolean => {
    switch (view) {
      case 'roles':
        return hasPermission(currentUser, 'roles:read');
      case 'users':
        return hasPermission(currentUser, 'users:read');
      case 'apikeys':
        return hasPermission(currentUser, 'apikeys:read');
      case 'backups':
        return hasPermission(currentUser, 'system:manageBackups');
      case 'inventory':
        return hasPermission(currentUser, 'system:manageInventory');
      case 'csv':
        return hasPermission(currentUser, 'csv:import') ||
          hasPermission(currentUser, 'csv:export') ||
          hasPermission(currentUser, 'csv:manageTemplates');
      case 'pptx-template-field-import':
        return hasPermission(currentUser, 'csv:import');
      case 'field-diagnostics':
        return hasPermission(currentUser, 'system:viewFieldDiagnostics');
      case 'favicon':
        return hasPermission(currentUser, 'system:manageBranding');
      case 'archive':
        return hasPermission(currentUser, 'assets:archive') ||
          hasPermission(currentUser, 'assets:update') ||
          hasPermission(currentUser, 'assets:delete');
      case 'trash':
        return hasPermission(currentUser, 'admin:all') || hasPermission(currentUser, 'trash:read');
      case 'duplicates':
        return hasPermission(currentUser, 'admin:all');
      case 'download-templates':
      case 'pdf-templates':
        return hasPermission(currentUser, 'downloadTemplates:read');
      case 'thumbnails':
        return hasPermission(currentUser, 'assets:update');
      case 'categories':
        return hasPermission(currentUser, 'categories:read');
      case 'badges':
        return hasPermission(currentUser, 'categories:create');
      case 'support-page':
        return hasPermission(currentUser, 'system:manageSettings');
      case 'login-page':
        return hasPermission(currentUser, 'system:manageLoginPage');
      case 'ai-queue':
        return hasPermission(currentUser, 'system:manageAiQueue');
      case 'popup':
        return hasPermission(currentUser, 'popup:read') ||
          hasPermission(currentUser, 'popup:create') ||
          hasPermission(currentUser, 'popup:update') ||
          hasPermission(currentUser, 'popup:delete') ||
          hasPermission(currentUser, 'popup:manageSettings');
      case 'brand-guide':
        return hasPermission(currentUser, 'brandGuide:update');
      case 'ppt-policies':
        return hasAnyPermission(currentUser, [
          'pptPolicies:update',
          'pptPolicies:publish',
          'pptPolicies:manageFiles',
        ]);
      case 'analytics':
        return hasPermission(currentUser, 'system:viewAnalytics');
      case 'maintenance':
        return hasPermission(currentUser, 'system:manageMaintenance');
      case 'imagemagick-test':
        return hasPermission(currentUser, 'system:manageImageTools');
      case 'issues-tasks':
        return hasPermission(currentUser, 'issues:read') || hasPermission(currentUser, 'tasks:read');
      case 'dabas-sync':
        return hasPermission(currentUser, 'system:manageDabasSync');
      case 'product-comparison':
        return canAccessProductComparison(currentUser);
      case 'product-image-validation':
        return hasPermission(currentUser, 'system:runProductImageValidation');
      case 'dev-snapshots':
        return hasAnyPermission(currentUser, ['admin:all', 'system:manageBackups']);
      case 'knowledge-hub':
        return KNOWLEDGE_HUB_UI_ENABLED && hasPermission(currentUser, 'knowledgeHub:admin');
      case 'wordpress-publishing':
        return hasPermission(currentUser, 'assets:update');
      default:
        return false;
    }
  }, [currentUser]);

  const getFirstAccessibleSettingsView = useCallback((): Exclude<SettingsView, null> | null => {
    return settingsViewOrder.find(view => canAccessSettingsView(view)) || null;
  }, [canAccessSettingsView, settingsViewOrder]);

  // Öppna settings direkt, använd hash om det finns, annars default till 'roles'
  const handleOpenSettings = () => {
    const hash = (window.location.hash || '').replace('#', '');
    const hashView = settingsViewOrder.includes(hash as Exclude<SettingsView, null>)
      ? hash as Exclude<SettingsView, null>
      : null;
    const targetView = hashView && canAccessSettingsView(hashView)
      ? hashView
      : getFirstAccessibleSettingsView();

    if (!targetView) return;

    setBrandGuideView(null);
    openSettingsView(targetView);
  };

  // Använd handleBackToMainView i relevanta hanterare
  const handleCloseSettings = () => {
    handleBackToMainView();
  };



  // Load saved recent searches from localStorage
  useEffect(() => {
    const savedSearches = localStorage.getItem('recentSearches');
    if (savedSearches) {
      try {
        setRecentSearches(JSON.parse(savedSearches));
      } catch (e) {
        console.error('Error parsing saved searches:', e);
      }
    }
  }, []);

  // Fetch categories on initial load and when a category is edited/deleted (only when logged in)
  useEffect(() => {
    if (currentUser) {
      fetchCategories();
    }
  }, [categoryToEdit, currentUser]);

  // Fetch templates once after login so chain filter can populate
  useEffect(() => {
    if (currentUser) {
      fetchTemplates();
    }
  }, [currentUser]);

  // Fetch all active badges once after login so badge filter is not limited to loaded assets
  useEffect(() => {
    if (!currentUser) return;
    getBadges().then(badges => setAllBadges(badges)).catch(() => {});
  }, [currentUser]);

  // Fetch popular AI tags when Mat (or descendant) category is active
  useEffect(() => {
    if (!matRelatedCategoryId) {
      setPopularAiTags([]);
      setPopularAiTagsLoading(false);
      return;
    }

    let isCancelled = false;
    const fetchPopularTags = async () => {
      try {
        setPopularAiTagsLoading(true);
        const response = await axiosInstance.get('/api/assets/popular-ai-tags', {
          params: { categoryId: matRelatedCategoryId, limit: 10 }
        });
        if (!isCancelled) {
          setPopularAiTags(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error('Error fetching popular AI tags:', error);
        if (!isCancelled) {
          setPopularAiTags([]);
        }
      } finally {
        if (!isCancelled) {
          setPopularAiTagsLoading(false);
        }
      }
    };

    fetchPopularTags();

    return () => {
      isCancelled = true;
    };
  }, [matRelatedCategoryId]);
  
  // Listen for category updates from CategoryManagementPage
  useEffect(() => {
    const handleCategoriesUpdated = () => {
      console.log('Categories updated event received, refreshing categories...');
      fetchCategories();
    };
    window.addEventListener('dam:categories-updated', handleCategoriesUpdated);
    return () => window.removeEventListener('dam:categories-updated', handleCategoriesUpdated);
  }, []);

  useEffect(() => {
    const validCategoryIds = new Set(categories.map((category) => category._id));
    setSelectedFilterCategoryIds((prevSelectedIds) => {
      const nextSelectedIds = prevSelectedIds.filter((categoryId) => validCategoryIds.has(categoryId));
      return nextSelectedIds.length === prevSelectedIds.length ? prevSelectedIds : nextSelectedIds;
    });
  }, [categories]);

  // Fetch collections on initial load and when a new collection is added (only when logged in)
  useEffect(() => {
    if (currentUser) {
      fetchCollections();
    }
  }, [currentUser, adminShowAllCollections]);

  // Fetch templates on initial load (only when logged in)
  useEffect(() => {
    if (currentUser) {
      fetchTemplates();
    }
  }, [currentUser]);

  // Clear current grid immediately on category change to avoid flash of previous category
  useEffect(() => {
    if (!currentUser) return;
    setAssets([]);
    setTotalAssetCount(undefined);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [currentUser, selectedFilterCategoryIds]);

  // Fetch assets when any search/filter state changes (debounced, coordinated)
  useEffect(() => {
    if (!currentUser) return;
    const categoryFilterKey = selectedFilterCategoryIds.join(',');
    const categoryChanged =
      previousCategoryFilterKeyRef.current !== null &&
      previousCategoryFilterKeyRef.current !== categoryFilterKey;
    previousCategoryFilterKeyRef.current = categoryFilterKey;

    if (categoryChanged) {
      console.log('Omedelbar fetch pga kategori-byte');
      fetchAssets({ mode: 'replace' });
      return;
    }

    const handle = setTimeout(() => {
      console.log('Koordinerad fetch pga uppdaterade sök/filter-parametrar');
      fetchAssets({ mode: 'replace' });
    }, 200); // lätt debounce för att undvika race/överdriven trafik
    return () => clearTimeout(handle);
  }, [
    currentUser,
    searchParams.searchTerm,
    searchParams.searchOperator,
    searchParams.fileTypes,
    searchParams.fromDate,
    searchParams.toDate,
    searchParams.favorite,
    searchParams.sortBy,
    searchParams.sortDirection,
    searchParams.searchFields,
    selectedFilterCategoryIds,
    selectedFilterBadgeIds,
    badgeFilterMode,
    selectedFilterTagNames,
    selectedFilterAiTagNames,
    chainFilterKey,
    wineFilterKey,
    kontorFilter,
    searchParams.collectionId
  ]);

  const hasMoreAssets = typeof totalAssetCount === 'number' && assets.length < totalAssetCount;
  const handleLoadMoreAssets = useCallback(() => {
    if (loadingAssets || !hasMoreAssets) return;
    fetchAssets({ mode: 'append' });
  }, [loadingAssets, hasMoreAssets, fetchAssets]);

  // When detail view opens, center the selected asset in the grid
  useEffect(() => {
    const detailOpen = !!selectedAssetId && !multiSelectEnabled;
    const wasDetailOpen = detailOpenRef.current;
    detailOpenRef.current = detailOpen;
    if (!detailOpen || wasDetailOpen) return;
    if (viewMode !== 'grid') return;
    const container = scrollContainerRef.current;
    if (!container || !selectedAssetId) return;

    const scrollToSelected = () => {
      const selectedCard = container.querySelector(
        `[data-asset-id="${selectedAssetId}"]`
      ) as HTMLElement | null;
      if (!selectedCard) return;
      const containerRect = container.getBoundingClientRect();
      const cardRect = selectedCard.getBoundingClientRect();
      const cardTop = cardRect.top - containerRect.top + container.scrollTop;
      const cardCenter = cardTop + (cardRect.height / 2);
      const targetScrollTop = cardCenter - (container.clientHeight / 2);
      const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
      container.scrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(scrollToSelected);
    });
  }, [multiSelectEnabled, selectedAssetId, viewMode]);

  // When multi-select is active, center the last selected asset in the grid
  useEffect(() => {
    if (!multiSelectEnabled) {
      lastCenteredMultiSelectIdRef.current = null;
      return;
    }
    if (viewMode !== 'grid') return;
    if (!selectionAnchorId) return;
    if (!selectedAssetIds.includes(selectionAnchorId)) return;
    if (lastCenteredMultiSelectIdRef.current === selectionAnchorId) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const anchorId = selectionAnchorId;
    lastCenteredMultiSelectIdRef.current = anchorId;

    const scrollToAnchor = () => {
      const selectedCard = container.querySelector(
        `[data-asset-id="${anchorId}"]`
      ) as HTMLElement | null;
      if (!selectedCard) return;
      const containerRect = container.getBoundingClientRect();
      const cardRect = selectedCard.getBoundingClientRect();
      const cardTop = cardRect.top - containerRect.top + container.scrollTop;
      const cardCenter = cardTop + (cardRect.height / 2);
      const targetScrollTop = cardCenter - (container.clientHeight / 2);
      const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
      container.scrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(scrollToAnchor);
    });
  }, [multiSelectEnabled, selectionAnchorId, selectedAssetIds, viewMode]);

  const handleAssetSelect = (assetId: string) => {
    setSelectedAssetId(assetId);
  };

  // Handle changes to asset selection in multi-select mode
  const handleSelectedAssetsChange = (selectedIds: string[]) => {
    setSelectedAssetIds(selectedIds);
    if (selectedIds.length <= 1 && !manualMultiSelectEnabledRef.current) {
      setMultiSelectEnabled(false);
    }
    
    // If only one asset is selected, also set it as the selected asset for details
    if (selectedIds.length === 1) {
      setSelectedAssetId(selectedIds[0]);
    } else if (selectedIds.length === 0) {
      setSelectedAssetId(null);
      setSelectionAnchorId(null);
    }
  };
  
  // Clear the multi-selection
  const handleClearSelection = () => {
    setSelectedAssetIds([]);
  };

  const handleCategoryAdded = () => {
    // Refresh categories after adding a new one
    fetchCategories();
  };

  const handleCollectionAdded = () => {
      // Refresh collections after adding a new one
      fetchCollections();
      handleCloseAddCollectionModal();
  };

  // Handle template actions
  const handleTemplateAdded = () => {
    fetchTemplates();
  };

  const handleTemplateUpdated = () => {
    fetchTemplates();
  };

  // Handle advanced search
  const handleAdvancedSearch = useCallback((params: AdvancedSearchParams) => {
    setSearchParams(params);
    setSelectedAssetId(null);
  }, []);

  const handleTabletSearchChange = useCallback((value: string) => {
    if (!isBrowseView) {
      navigateToBrowseView();
    }
    setSearchParams(prev => ({ ...prev, searchTerm: value }));
    setSelectedAssetId(null);
  }, [isBrowseView, navigateToBrowseView]);

  const handleOpenTabletFilters = useCallback(() => {
    if (!isBrowseView) {
      navigateToBrowseView();
    }
    setSelectedAssetId(null);
    setTabletFiltersOpen(true);
  }, [isBrowseView, navigateToBrowseView]);

  // Saved searches – App-level snapshot
  type SavedSnapshot = {
    name: string;
    createdAt: number;
    searchParams: AdvancedSearchParams;
    categories: string[];
    tags: string[];
    aiTags: string[];
    badges: string[];
    badgeFilterMode?: 'AND' | 'OR';
  };
  const [savedSnapshots, setSavedSnapshots] = useState<SavedSnapshot[]>(() => {
    try {
      const raw = localStorage.getItem('savedSearchSnapshots');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const saveCurrentSnapshot = () => {
    const name = window.prompt('Namnge sökningen');
    if (!name) return;
    const snapshot: SavedSnapshot = {
      name,
      createdAt: Date.now(),
      searchParams,
      categories: selectedFilterCategoryIds,
      tags: selectedFilterTagNames,
      aiTags: selectedFilterAiTagNames,
      badges: selectedFilterBadgeIds,
      badgeFilterMode: badgeFilterMode,
    };
    const updated = [snapshot, ...savedSnapshots.filter(s => s.name !== name)].slice(0, 50);
    setSavedSnapshots(updated);
    localStorage.setItem('savedSearchSnapshots', JSON.stringify(updated));
  };

  const applySnapshot = (s: SavedSnapshot) => {
    setSearchParams(s.searchParams);
    setSelectedFilterCategoryIds(s.categories);
    setSelectedFilterTagNames(s.tags);
    setSelectedFilterAiTagNames(s.aiTags);
    setSelectedFilterBadgeIds(s.badges);
    setBadgeFilterMode(s.badgeFilterMode || 'AND');
  };

  const deleteSnapshot = (idx: number) => {
    const updated = savedSnapshots.filter((_, i) => i !== idx);
    setSavedSnapshots(updated);
    localStorage.setItem('savedSearchSnapshots', JSON.stringify(updated));
  };

  const handleEditCategory = (category: ICategory) => {
    setCategoryToEdit(category);
  };

  const handleCloseEditModal = () => {
    setCategoryToEdit(null);
    // Re-fetch categories after closing modal, in case of changes
    fetchCategories();
  };

  // Handle asset deletion
  const handleAssetDeleted = (deletedAssetId: string) => {
    console.log('Asset deleted:', deletedAssetId);
    
    // Clear the selection if the deleted asset was selected
    if (selectedAssetId === deletedAssetId) {
      setSelectedAssetId(null);
    }
    
    // Remove from multi-selection if present
    if (selectedAssetIds.includes(deletedAssetId)) {
      setSelectedAssetIds(prev => prev.filter(id => id !== deletedAssetId));
    }
    
    // Refresh assets
    fetchAssets();
  };

  const handleOpenUploadModal = () => {
    setIsUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  // Handlers for Enhanced Upload Modal
  const handleOpenEnhancedUploadModal = () => {
    setIsEnhancedUploadModalOpen(true);
  };

  const handleCloseEnhancedUploadModal = () => {
    setIsEnhancedUploadModalOpen(false);
  };

  // Handlers for Add Category Modal
  const handleOpenAddCategoryModal = () => {
    setIsAddCategoryModalOpen(true);
  };

  const handleCloseAddCategoryModal = () => {
    setIsAddCategoryModalOpen(false);
    // Optional: Refresh categories list after adding a category
    fetchCategories(); // Assuming fetchCategories should be called after add
  };

  // Handler to open Categories settings page
  const handleOpenCategoriesSettings = () => {
      openSettingsView('categories');
  };

  // Handlers for Add Collection Modal
  const handleOpenAddCollectionModal = () => {
      setIsAddCollectionModalOpen(true);
  };

  const handleCloseAddCollectionModal = () => {
      setIsAddCollectionModalOpen(false);
  };

  // Handlers for Template Management Modal
  const handleOpenTemplateManagementModal = () => {
    setIsTemplateManagementModalOpen(true);
  };

  const handleCloseTemplateManagementModal = () => {
    setIsTemplateManagementModalOpen(false);
  };

  const handleOpenBadgesSettings = () => {
    openSettingsView('badges');
  };

  const handleOpenBrandGuideLanding = () => {
    setFullPageAssetRoute(null);
    setCurrentSettingsView(null);
    setShowCollectionDetail(false);
    setBrandGuideEditorSlug(null);
    setBrandGuideView({ type: 'landing' });
    setShowPptPolicies(false);
    setShowBriefs(false);
    setShowPrompts(false);
    setShowAiImageStudio(false);
    setShowRecipeCorrection(false);
    setShowInhouse(false);
    setShowListningar(false);
    setShowNyhetslanseringar(false);
    setShowBarcodeGenerator(false);
    setShowTrends(false);
    setShowPresentations(false);
  };

  const handleOpenTrends = () => {
    if (!currentUser || !hasPermission(currentUser, 'trends:read')) {
      return;
    }
    setCurrentSettingsView(null);
    setShowCollectionDetail(false);
    setBrandGuideView(null);
    setShowPptPolicies(false);
    setShowBriefs(false);
    setShowPrompts(false);
    setShowAiImageStudio(false);
    setShowRecipeCorrection(false);
    setShowInhouse(false);
    setShowListningar(false);
    setShowNyhetslanseringar(false);
    setShowBarcodeGenerator(false);
    setShowPresentations(false);
    setShowTrends(true);
  };

  const handleCloseTrends = () => {
    setShowTrends(false);
  };

  const handleCloseBriefs = () => {
    setShowBriefs(false);
  };

  const handleOpenPrompts = () => {
    if (!currentUser || (!hasPermission(currentUser, 'prompts:use') && !hasPermission(currentUser, 'prompts:admin'))) {
      return;
    }
    setCurrentSettingsView(null);
    setShowCollectionDetail(false);
    setBrandGuideView(null);
    setShowPptPolicies(false);
    setShowBriefs(false);
    setShowPrompts(true);
    setShowAiImageStudio(false);
    setShowRecipeCorrection(false);
    setShowInhouse(false);
    setShowListningar(false);
    setShowNyhetslanseringar(false);
    setShowBarcodeGenerator(false);
    setShowTrends(false);
    setShowPresentations(false);
  };

  const handleClosePrompts = () => {
    setShowPrompts(false);
  };

  const handleOpenAiImageStudio = (recipeAssetId: string | null = null, editAssetId: string | null = null) => {
    if (
      !currentUser ||
      (
        !hasPermission(currentUser, 'aiImages:use') &&
        !hasPermission(currentUser, 'aiImages:review') &&
        !hasPermission(currentUser, 'aiImages:publish') &&
        !hasPermission(currentUser, 'aiImages:admin')
      )
    ) {
      return;
    }
    setInitialAiRecipeAssetId(recipeAssetId);
    setInitialAiEditAssetId(editAssetId);
    setCurrentSettingsView(null);
    setShowCollectionDetail(false);
    setBrandGuideView(null);
    setShowPptPolicies(false);
    setShowBriefs(false);
    setShowPrompts(false);
    setShowAiImageStudio(true);
    setShowRecipeCorrection(false);
    setShowInhouse(false);
    setShowListningar(false);
    setShowNyhetslanseringar(false);
    setShowBarcodeGenerator(false);
    setShowTrends(false);
    setShowPresentations(false);
  };

  const handleOpenAiImageStudioForRecipeAsset = (assetId: string) => {
    handleOpenAiImageStudio(assetId, null);
  };

  const handleOpenAiImageStudioForEditAsset = (assetId: string) => {
    handleOpenAiImageStudio(null, assetId);
  };

  const handleOpenRecipeCorrection = (assetId: string, options?: { snapshot?: boolean }) => {
    if (
      !currentUser ||
      (
        !hasPermission(currentUser, 'recipeCorrection:use') &&
        !hasPermission(currentUser, 'recipeCorrection:admin')
      )
    ) {
      return;
    }
    setRecipeCorrectionAssetId(assetId);
    setRecipeCorrectionOpenedFromFullPage(Boolean(fullPageAssetRoute));
    setRecipeCorrectionSnapshotMode(Boolean(options?.snapshot));
    setCurrentSettingsView(null);
    setShowCollectionDetail(false);
    setBrandGuideView(null);
    setShowPptPolicies(false);
    setShowBriefs(false);
    setShowPrompts(false);
    setShowAiImageStudio(false);
    setShowRecipeCorrection(true);
    setShowInhouse(false);
    setShowListningar(false);
    setShowNyhetslanseringar(false);
    setShowBarcodeGenerator(false);
    setShowTrends(false);
    setShowPresentations(false);
  };

  const handleCloseRecipeCorrection = () => {
    setShowRecipeCorrection(false);
    setRecipeCorrectionSnapshotMode(false);
  };

  const handleRecipeCorrectionApplied = (assetId: string) => {
    setShowRecipeCorrection(false);
    setRecipeCorrectionSnapshotMode(false);
    setFullPageRemountKey((value) => value + 1);
    openFullPageAssetById(assetId, {
      hasBrowseContext: fullPageAssetRoute?.hasBrowseContext === true || recipeCorrectionOpenedFromFullPage,
      replace: fullPageAssetRoute?.assetId === assetId,
    });
    handleAssetUpdate();
    setRecipeSavedNotice(true);
  };

  const handleCloseAiImageStudio = () => {
    setShowAiImageStudio(false);
    setShowRecipeCorrection(false);
  };

  const handleOpenInhouse = () => {
    setCurrentSettingsView(null);
    setShowCollectionDetail(false);
    setBrandGuideView(null);
    setShowPptPolicies(false);
    setShowBriefs(false);
    setShowPrompts(false);
    setShowAiImageStudio(false);
    setShowRecipeCorrection(false);
    setShowInhouse(true);
    setShowListningar(false);
    setShowNyhetslanseringar(false);
    setShowBarcodeGenerator(false);
    setShowTrends(false);
    setShowPresentations(false);
  };

  const handleCloseInhouse = () => {
    setShowInhouse(false);
  };

  const handleOpenListningar = () => {
    setCurrentSettingsView(null);
    setShowCollectionDetail(false);
    setBrandGuideView(null);
    setShowPptPolicies(false);
    setShowBriefs(false);
    setShowPrompts(false);
    setShowAiImageStudio(false);
    setShowRecipeCorrection(false);
    setShowInhouse(false);
    setShowListningar(true);
    setShowNyhetslanseringar(false);
    setShowBarcodeGenerator(false);
    setShowTrends(false);
    setShowPresentations(false);
  };

  const handleCloseListningar = () => {
    setShowListningar(false);
  };

  const handleOpenNyhetslanseringar = () => {
    setCurrentSettingsView(null);
    setShowCollectionDetail(false);
    setBrandGuideView(null);
    setShowPptPolicies(false);
    setShowBriefs(false);
    setShowPrompts(false);
    setShowAiImageStudio(false);
    setShowRecipeCorrection(false);
    setShowInhouse(false);
    setShowListningar(false);
    setShowNyhetslanseringar(true);
    setShowBarcodeGenerator(false);
    setShowTrends(false);
    setShowPresentations(false);
  };

  const handleCloseNyhetslanseringar = () => {
    setShowNyhetslanseringar(false);
  };

  const handleOpenBarcodeGenerator = () => {
    if (!currentUser || !hasPermission(currentUser, 'barcode:access')) {
      return;
    }
    setCurrentSettingsView(null);
    setShowCollectionDetail(false);
    setBrandGuideView(null);
    setShowPptPolicies(false);
    setShowBriefs(false);
    setShowPrompts(false);
    setShowAiImageStudio(false);
    setShowRecipeCorrection(false);
    setShowInhouse(false);
    setShowListningar(false);
    setShowNyhetslanseringar(false);
    setShowBarcodeGenerator(true);
    setShowTrends(false);
    setShowPresentations(false);
  };

  const handleCloseBarcodeGenerator = () => {
    setShowBarcodeGenerator(false);
  };

  const handleOpenBrandGuideSection = (slug: string) => {
    setBrandGuideView({ type: 'section', slug });
  };

  const handleCloseBrandGuide = () => {
    setBrandGuideView(null);
  };

  const handleOpenBrandGuideEditor = (slug?: string | null) => {
    setBrandGuideEditorSlug(slug || null);
    openSettingsView('brand-guide');
    setBrandGuideView(null);
  };

  const handleOpenPptPolicies = () => {
    setCurrentSettingsView(null);
    setShowCollectionDetail(false);
    setBrandGuideView(null);
    setBrandGuideEditorSlug(null);
    setShowPptPolicies(true);
    setShowBriefs(false);
    setShowPrompts(false);
    setShowAiImageStudio(false);
    setShowRecipeCorrection(false);
    setShowInhouse(false);
    setShowListningar(false);
    setShowNyhetslanseringar(false);
    setShowBarcodeGenerator(false);
    setShowTrends(false);
    setShowPresentations(false);
  };

  const handleClosePptPolicies = () => {
    setShowPptPolicies(false);
  };

  const handleOpenPresentations = () => {
    if (!currentUser || !hasPermission(currentUser, 'presentations:read')) {
      return;
    }
    setCurrentSettingsView(null);
    setShowCollectionDetail(false);
    setBrandGuideView(null);
    setBrandGuideEditorSlug(null);
    setShowPptPolicies(false);
    setShowBriefs(false);
    setShowPrompts(false);
    setShowAiImageStudio(false);
    setShowRecipeCorrection(false);
    setShowInhouse(false);
    setShowListningar(false);
    setShowNyhetslanseringar(false);
    setShowBarcodeGenerator(false);
    setShowTrends(false);
    setShowPresentations(true);
  };

  const handleClosePresentations = () => {
    setShowPresentations(false);
  };

  const handleOpenAssetBadgeManager = (asset: IAsset | null, assetIds?: string[]) => {
    if (assetIds && assetIds.length > 0) {
      setBadgeManagerAssetIds(assetIds);
      if (assetIds.length === 1) {
        if (asset) {
          setAssetForBadgeManagement(asset);
        } else {
          const fallbackAsset = assets.find(a => a._id === assetIds[0]) || null;
          setAssetForBadgeManagement(fallbackAsset);
        }
      } else {
        setAssetForBadgeManagement(null);
      }
    } else {
      setBadgeManagerAssetIds(null);
      setAssetForBadgeManagement(asset);
    }
    setIsAssetBadgeManagerOpen(true);
  };

  const handleCloseAssetBadgeManager = () => {
    setIsAssetBadgeManagerOpen(false);
    setAssetForBadgeManagement(null);
    setBadgeManagerAssetIds(null);
  };

  const handleBadgeUpdated = () => {
    fetchAssets();
    getBadges().then(badges => setAllBadges(badges)).catch(() => {});
  };

  // Handler for Category Filter button toggle
  const handleCategoryFilterToggle = useCallback((categoryId: string) => {
    setSelectedFilterCategoryIds(prevSelectedIds => {
      if (prevSelectedIds.includes(categoryId)) {
        // Category is already selected, remove it
        return prevSelectedIds.filter(id => id !== categoryId);
      } else {
        // Category is not selected, add it
        return [...prevSelectedIds, categoryId];
      }
    });
  }, []);

  // Handler for Badge Filter button toggle
  const handleBadgeFilterToggle = useCallback((badgeId: string) => {
    setSelectedFilterBadgeIds(prevSelectedIds => {
      if (prevSelectedIds.includes(badgeId)) {
        return prevSelectedIds.filter(id => id !== badgeId);
      } else {
        return [...prevSelectedIds, badgeId];
      }
    });
  }, []);

  // Clear all badge filters and scroll to top
  const handleClearBadgeFilters = useCallback(() => {
    setSelectedFilterBadgeIds([]);
  }, []);

  // Scroll to top whenever the badge filter selection changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [selectedFilterBadgeIds]);

  const handleAiTagChipToggle = useCallback((tag: string) => {
    setSelectedFilterAiTagNames(prevSelectedTags => {
      const normalized = tag.toLowerCase();
      const exists = prevSelectedTags.some(existing => existing.toLowerCase() === normalized);
      if (exists) {
        return prevSelectedTags.filter(existing => existing.toLowerCase() !== normalized);
      }
      return [...prevSelectedTags, tag];
    });
  }, []);

  const handleAssetUploadSuccess = () => {
    console.log('Upload success callback triggered, closing upload dialogs...');
    setIsUploadModalOpen(false);
    setIsEnhancedUploadModalOpen(false);
    setTimeout(() => {
      fetchCategories(); // Categories might be automatically generated/assigned
    }, 200);
  };

  const handleAddCategorySuccess = () => {
    setIsAddCategoryModalOpen(false);
    fetchCategories(); // Refresh category list
  };

  const handleRemoveFromCollection = async (assetIds: string[]) => {
    if (!selectedCollectionId || assetIds.length === 0) return;
    try {
      await Promise.all(
        assetIds.map(id =>
          axiosInstance.delete(`/api/collections/${selectedCollectionId}/assets/${id}`)
        )
      );
      fetchAssets();
      fetchCollections();
    } catch (err) {
      console.error('Error removing assets from collection:', err);
    }
  };

  const handleCollectionSelected = (collectionId: string | null) => {
    setSelectedCollectionId(collectionId);
    setShowCollectionDetail(false);
    
    if (collectionId) {
      setSearchParams(prev => ({ ...prev, collectionId }));
    } else {
      setSearchParams(prev => {
        const newParams = { ...prev };
        delete newParams.collectionId;
        return newParams;
      });
    }
  };

  // Handler for bulk operation completion
  const handleBulkOperationComplete = () => {
    // Refresh assets and categories after bulk operations
    fetchAssets();
    fetchCategories();
  };

  // Toggle multi-select mode
  const handleToggleMultiSelect = () => {
    setMultiSelectEnabled(prev => {
      const next = !prev;
      manualMultiSelectEnabledRef.current = next;
      return next;
    });
    // Clear selection when disabling multi-select
    if (multiSelectEnabled) {
      setSelectedAssetIds([]);
    }
  };

  // Handler for regenerating thumbnails
  const handleRegenerateThumbnails = async () => {
    if (regeneratingThumbnails) return;
    try {
      setRegeneratingThumbnails(true);
      setRegenerateMessage('Regenererar thumbnails, vänta…');

      // Correlate this run early for polling fallback
      const runId = `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
      regenAllRunIdRef.current = runId;
      setRegenAllDialogOpen(true);
      setRegenAllProgress({ runId, current: 0, total: 0, success: 0, errors: 0 });

      // Setup SSE listener for progress (scope: all) BEFORE starting job
      try {
        const token = localStorage.getItem('authToken');
        const url = token ? `/api/assets/thumbnails/events?token=${encodeURIComponent(token)}` : '/api/assets/thumbnails/events';
        const es = new EventSource(url);
        regenAllEsRef.current = es;
        es.onmessage = (evt) => {
          try {
            const data = JSON.parse(evt.data);
            if (!data || data.scope !== 'all') return;
            if (data.type === 'start') {
              regenAllRunIdRef.current = data.runId || regenAllRunIdRef.current;
              setRegenAllDialogOpen(true);
              setRegenAllProgress({ runId: regenAllRunIdRef.current, current: 0, total: data.total || 0, success: 0, errors: 0 });
            } else if (data.type === 'progress') {
              if (data.runId && regenAllRunIdRef.current && data.runId !== regenAllRunIdRef.current) return;
              setRegenAllProgress({
                runId: regenAllRunIdRef.current,
                current: data.current,
                total: data.total,
                success: data.success,
                errors: data.errors,
                fileName: data.fileName,
                etaSeconds: data.etaSeconds
              });
            } else if (data.type === 'done') {
              if (data.runId && regenAllRunIdRef.current && data.runId !== regenAllRunIdRef.current) return;
              setRegenAllProgress((prev) => prev ? { ...prev, current: data.total, success: data.success, errors: data.errors } : null);
              setRegenerateMessage(`Thumbnails regenererade: OK ${data.success}, Fel ${data.errors}.`);
              setTimeout(() => setRegenAllDialogOpen(false), 1200);
            }
          } catch {}
        };
        const startPolling = () => {
          if (regenAllPollIntervalRef.current || !regenAllRunIdRef.current) return;
          try {
            const token2 = localStorage.getItem('authToken');
            regenAllPollIntervalRef.current = setInterval(async () => {
              try {
                const status = await axiosInstance.get(`/api/assets/thumbnails/status`, { params: { runId: regenAllRunIdRef.current, token: token2 } });
                const s = status.data || {};
                if (!s || !s.total) return;
                setRegenAllProgress(prev => ({
                  runId: regenAllRunIdRef.current,
                  current: s.current || 0,
                  total: s.total || (prev?.total || 0),
                  success: s.success || 0,
                  errors: s.errors || 0,
                  fileName: s.fileName,
                  etaSeconds: (() => {
                    const started = s.startedAt || 0;
                    const updated = s.updatedAt || Date.now();
                    const elapsed = Math.max(1, (updated - started) / 1000);
                    const rate = (s.current || 0) / elapsed;
                    const remaining = Math.max(0, (s.total || 0) - (s.current || 0));
                    return Math.round(remaining / Math.max(0.01, rate));
                  })()
                }));
                if (s.done) {
                  clearInterval(regenAllPollIntervalRef.current);
                  regenAllPollIntervalRef.current = null;
                  setRegenerateMessage(`Thumbnails regenererade: OK ${s.success}, Fel ${s.errors}.`);
                  setTimeout(() => setRegenAllDialogOpen(false), 1200);
                }
              } catch {}
            }, 1000);
          } catch {}
        };
        // Start polling immediately and keep SSE in parallel
        startPolling();
        es.onerror = startPolling;
      } catch {}

      const response = await axiosInstance.post(`/api/assets/regenerate-thumbnails`, { runId });
      setRegenerateMessage(`Thumbnail regeneration complete: ${response.data.success} succeeded, ${response.data.errors} failed.`);
      fetchAssets();
      setTimeout(() => setRegenerateMessage(null), 5000);
    } catch (error: any) {
      setRegenerateMessage(`Error regenerating thumbnails: ${error.response?.data?.message || error.message}`);
    } finally {
      setRegeneratingThumbnails(false);
      if (regenAllEsRef.current) { try { regenAllEsRef.current.close(); } catch {} }
      if (regenAllPollIntervalRef.current) { clearInterval(regenAllPollIntervalRef.current); regenAllPollIntervalRef.current = null; }
    }
  };

  // Handle thumbnail size change
  const handleThumbnailSizeChange = (event: Event, newValue: number | number[]) => {
    setThumbnailSize(newValue as number);
  };

  // Handle view mode change
  const handleViewModeChange = (mode: 'grid' | 'list') => {
    setViewMode(mode);
  };

  // Function to handle adding assets to a collection via drag and drop
  const handleDropOnCollection = async (collectionId: string, assetIds: string[]) => {
    try {
      await axiosInstance.post(`/api/collections/${collectionId}/assets/bulk`, {
        assetIds
      });
      
      // Refresh collections
      fetchCollections();
    } catch (err: any) {
      console.error('Error adding assets to collection:', err);
      alert(`Error adding assets to collection: ${err.message}`);
    }
  };

  // Function to handle sharing a collection
  const handleShareCollection = (collection: ICollection) => {
    setCollectionToShare(collection);
    setShareCollectionDialogOpen(true);
    console.log("ShareCollectionDialog temporarily disabled. Collection to share:", collection);
  };

  // Function to checkLoggedIn with improved token handling
  const checkLoggedIn = async () => {
    setAuthLoading(true);
    try {
      const token = getAuthToken();
      console.log('App startup: Token status:', token ? 'Hittad' : 'Saknas');
      
      if (token) {
        console.log('Försöker hämta användardata med befintlig token...');
        try {
          // Hämta användardata med befintlig token
          const response = await getMe();
          
          if (response.data?.user) {
            console.log('Inloggning med sparad token lyckades - användare:', response.data.user.email);
            setCurrentUser(response.data.user);
          } else {
            console.warn('Misslyckades med att hämta användardata med befintlig token:', response.error);
            await authLogout();
            setCurrentUser(null);
          }
        } catch (userError) {
          console.error('Fel vid hämtning av användardata:', userError);
          await authLogout();
          setCurrentUser(null);
        }
      } else {
        console.log('Ingen token hittad, användaren är inte inloggad');
        setCurrentUser(null);
      }
    } catch (error) {
      console.error('Allvarligt fel vid inloggningskontroll:', error);
      await authLogout(); // Säkerställ att token rensas
      setCurrentUser(null);
    } finally {
      setAuthLoading(false);
    }
  };

  // Modifiera useEffect för att köra checkLoggedIn ENDAST vid uppstart
  useEffect(() => {
    // Kontrollera token vid start - körs bara en gång vid komponentmontering
    checkLoggedIn();
    // Ingen extra window.load-forcering – fetchAssets triggas via currentUser-effect

    // Suppress ResizeObserver errors globally
    const resizeObserverErrHandler = (e: ErrorEvent) => {
      if (e.message && (
        e.message.includes('ResizeObserver loop') ||
        e.message.includes('ResizeObserver') ||
        e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
        e.message === 'ResizeObserver loop limit exceeded'
      )) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    };
    window.addEventListener('error', resizeObserverErrHandler);
    return () => window.removeEventListener('error', resizeObserverErrHandler);
  }, []); // Ta bort currentUser från beroendet!
  
  // Ta bort extra initial-fetch: search/filter-effekten nedan hämtar när currentUser finns

  // Listen to upload events to auto-refresh grid
  useEffect(() => {
    const triggerFetch = () => {
      try { return fetchAssetsLatestRef.current(); } catch { return Promise.resolve(); }
    };
    const onUploaded = () => {
      triggerFetch();
      void refreshWordPressPendingAssetCount();
    };
    const onBatchDone = (event: Event) => {
      const detail = (event as CustomEvent<UploadBatchCompletedDetail>).detail;
      setUploadToast({ open: true, message: 'Uppladdning klar – uppdaterar gridden…', severity: 'success' });
      triggerFetch();
      void refreshWordPressPendingAssetCount();
      maybeRunProductImageValidationForUpload(detail);
    };
    const onArchived = (e: any) => {
      // Snabb lokalfiltning: ta bort id ur state direkt
      const id = e?.detail?.id;
      if (id) {
        setAssets(prev => prev.filter(a => a._id !== id));
      }
      // Säkerställ att backend-filtering också slår igenom
      triggerFetch();
      void refreshWordPressPendingAssetCount();
    };
    const onAssetsUpdated = () => {
      void refreshWordPressPendingAssetCount();
    };
    const onWordPressSummaryUpdated = (event: Event) => {
      if (!hasPermission(currentUser, 'admin:all')) return;
      const detail = (event as CustomEvent<{ pendingAssetCount?: number }>).detail;
      const nextCount = Number(detail?.pendingAssetCount);
      if (Number.isFinite(nextCount)) {
        setWordpressPendingAssetCount(Math.max(0, nextCount));
      } else {
        void refreshWordPressPendingAssetCount();
      }
    };
    window.addEventListener('dam:asset-uploaded', onUploaded);
    window.addEventListener('dam:upload-batch-completed', onBatchDone);
    window.addEventListener('dam:asset-archived', onArchived as any);
    window.addEventListener('dam:assets-updated', onAssetsUpdated);
    window.addEventListener('bulk-edit-completed', onAssetsUpdated);
    window.addEventListener('wordpress-publishing:summary-updated', onWordPressSummaryUpdated);
    return () => {
      window.removeEventListener('dam:asset-uploaded', onUploaded);
      window.removeEventListener('dam:upload-batch-completed', onBatchDone);
      window.removeEventListener('dam:asset-archived', onArchived as any);
      window.removeEventListener('dam:assets-updated', onAssetsUpdated);
      window.removeEventListener('bulk-edit-completed', onAssetsUpdated);
      window.removeEventListener('wordpress-publishing:summary-updated', onWordPressSummaryUpdated);
    };
  }, [currentUser, maybeRunProductImageValidationForUpload, refreshWordPressPendingAssetCount]);

  // SSE: lyssna på AI‑events och uppdatera listan utan blink
  useEffect(() => {
    let es: EventSource | null = null;
    try {
      const token = localStorage.getItem('authToken');
      const url = token ? `/api/assets/ai/events?token=${encodeURIComponent(token)}` : '/api/assets/ai/events';
      es = new EventSource(url);
      es.onmessage = () => {
        fetchAssets();
      };
    } catch {}
    return () => { if (es) es.close(); };
  }, []);

  const handleLoginSuccess = (user: IUser) => {
    console.log('Login success callback triggered med användare:', user.email);
    
    // Först sätt användaren
    setCurrentUser(user);
    
    // Vänta en stund för att säkerställa att token verkligen sparats innan assets hämtas
    setTimeout(async () => {
      try {
        // Verifiera explicit att token finns
        const token = getAuthToken();
        if (token) {
          console.log('Verifierad token finns tillgänglig efter inloggning, hämtar assets...');
          // Hämta kategorier först för att säkerställa att de finns tillgängliga
          await fetchCategories();
          // Sedan hämta assets
          await fetchAssets();
          console.log('Assets hämtade efter inloggning');
          
          // Check if popup should be shown
          const popupDismissed = localStorage.getItem('popup_dismissed') === 'true';
          if (!popupDismissed) {
            // Check popup settings from API
            try {
              const settingsRes = await axiosInstance.get('/api/popup/settings');
              if (settingsRes.data?.enabled) {
                const itemsRes = await axiosInstance.get('/api/popup/items/active');
                if (itemsRes.data?.length > 0) {
                  setShowPopupNews(true);
                }
              }
            } catch (e) {
              console.log('Popup settings not available:', e);
            }
          }
        } else {
          console.error('Token saknas trots lyckad inloggning!');
        }
      } catch (error) {
        console.error('Fel vid datahämtning efter inloggning:', error);
      }
    }, 800); // Öka fördröjningen till 800ms för att säkerställa att token är helt sparad
  };

  const resetAfterLogout = useCallback(() => {
    setCurrentUser(null);
    setBrandGuideView(null);
    setBrandGuideEditorSlug(null);
    // Rensa eventuell hash så att F5/Login inte hoppar tillbaka till settings
    try {
      const cleanUrl = window.location.pathname + window.location.search;
      window.history.replaceState(null, '', cleanUrl);
    } catch {}
  }, []);

  const handleLogout = async (options?: { reason?: string; server?: boolean }) => {
    await authLogout(options);
    resetAfterLogout();
  };

  useAuthSessionLifecycle(currentUser, () => {
    resetAfterLogout();
  });

  // Close asset detail and clear selection highlight in grid
  const closeAssetDetails = () => {
    setSelectedAssetId(null);
    setSelectedAssetIds([]);
  };

  const syncFullPageAssetRouteFromLocation = useCallback(() => {
    const nextRoute = getFullPageAssetRouteFromLocation();
    setFullPageAssetRoute(nextRoute);
    if (nextRoute) {
      setSelectedAssetId(nextRoute.assetId);
    }
  }, []);

  const syncBrandGuideViewFromLocation = useCallback(() => {
    setBrandGuideView(isBrandGuideLandingPath() ? { type: 'landing' } : null);
  }, []);

  useEffect(() => {
    const onPopState = () => {
      syncFullPageAssetRouteFromLocation();
      syncBrandGuideViewFromLocation();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [syncBrandGuideViewFromLocation, syncFullPageAssetRouteFromLocation]);

  useEffect(() => {
    const onBrandGuidePath = isBrandGuideLandingPath();
    if (brandGuideView) {
      if (!onBrandGuidePath) {
        try {
          window.history.pushState(window.history.state, '', BRAND_GUIDE_LANDING_PATH);
        } catch {}
      }
      return;
    }

    if (onBrandGuidePath) {
      try {
        const nextUrl = `/${window.location.search}${window.location.hash}`;
        window.history.replaceState(window.history.state, '', nextUrl);
      } catch {}
    }
  }, [brandGuideView]);

  const clearFullPageAssetRoute = useCallback((options?: { replacePath?: string }) => {
    try {
      const nextState = { ...(window.history.state || {}) };
      delete nextState[FULL_PAGE_ASSET_HISTORY_KEY];
      window.history.replaceState(nextState, '', options?.replacePath || '/');
    } catch {}
    setFullPageAssetRoute(null);
  }, []);

  const openFullPageAssetById = useCallback((assetId: string, options?: { hasBrowseContext?: boolean; replace?: boolean }) => {
    const normalizedAssetId = String(assetId || '').trim();
    if (!normalizedAssetId) return;

    const nextRoute: FullPageAssetRoute = {
      assetId: normalizedAssetId,
      hasBrowseContext: options?.hasBrowseContext === true,
    };
    const nextPath = buildFullPageAssetPath(normalizedAssetId);
    const nextState = {
      ...(window.history.state || {}),
      [FULL_PAGE_ASSET_HISTORY_KEY]: {
        ...nextRoute,
        openedFromApp: true,
      },
    };

    try {
      if (options?.replace === true) {
        window.history.replaceState(nextState, '', nextPath);
      } else {
        window.history.pushState(nextState, '', nextPath);
      }
    } catch {}

    try { window.location.hash = ''; } catch {}
    setCurrentSettingsView(null);
    setShowCollectionDetail(false);
    setBrandGuideView(null);
    setShowBriefs(false);
    setShowPrompts(false);
    setShowAiImageStudio(false);
    setShowRecipeCorrection(false);
    setShowInhouse(false);
    setShowListningar(false);
    setShowNyhetslanseringar(false);
    setShowBarcodeGenerator(false);
    setShowTrends(false);
    setShowPresentations(false);
    manualMultiSelectEnabledRef.current = false;
    setMultiSelectEnabled(false);
    setSelectedAssetIds([]);
    setSelectedAssetId(normalizedAssetId);
    setFullPageAssetRoute(nextRoute);
  }, []);

  const closeFullPageAsset = useCallback(() => {
    const routeState = window.history.state?.[FULL_PAGE_ASSET_HISTORY_KEY];
    if (getFullPageAssetId() && routeState?.openedFromApp) {
      window.history.back();
      return;
    }

    clearFullPageAssetRoute({ replacePath: '/' });
  }, [clearFullPageAssetRoute]);

  const handleFullPageAssetChange = useCallback((newAssetId: string) => {
    openFullPageAssetById(newAssetId, {
      hasBrowseContext: fullPageAssetRoute?.hasBrowseContext === true,
      replace: false,
    });
  }, [fullPageAssetRoute?.hasBrowseContext, openFullPageAssetById]);

  // Add function to handle API key settings
  const handleOpenApiKeys = () => {
    openSettingsView('apikeys');
  };

  // Handler for syncing all assets with Dabas
  const handleSyncAllDabas = async () => {
    if (dabasSyncing) return;
    
    // Open dialog and reset state
    setDabasSyncDialogOpen(true);
    setDabasSyncResults(null);
    setShowDabasSuccessList(false);
    
    try {
      setDabasSyncing(true);
      
      const response = await axiosInstance.post('/api/external/sync-all-assets?system=dabas');
      
      if (response.data) {
        setDabasSyncResults({
          totalAssets: response.data.totalAssets || 0,
          successCount: response.data.successCount || 0,
          failedCount: response.data.failedCount || 0,
          successes: response.data.successes || [],
          failures: response.data.failures || []
        });
        // Refresh assets after sync
        await fetchAssets();
      }
    } catch (error: any) {
      console.error('Error syncing with Dabas:', error);
      setDabasSyncResults({
        totalAssets: 0,
        successCount: 0,
        failedCount: 1,
        successes: [],
        failures: [{ assetId: '-', message: error.response?.data?.message || error.message }]
      });
    } finally {
      setDabasSyncing(false);
    }
  };

  const handleOpenBackups = () => {
    openSettingsView('backups');
  };

  const handleOpenInventory = () => {
    if (!currentUser || !hasPermission(currentUser, 'system:manageInventory')) {
      return;
    }
    setShowCollectionDetail(false);
    openSettingsView('inventory');
  };

  const handleOpenCsv = () => {
    setShowCollectionDetail(false);
    openSettingsView('csv');
  };

  const handleOpenFieldDiagnostics = () => {
    setShowCollectionDetail(false);
    openSettingsView('field-diagnostics');
  };

  const handleOpenIssuesTasks = () => {
    setShowCollectionDetail(false);
    openSettingsView('issues-tasks');
  };

  const handleReportBug = () => {
    setShowCollectionDetail(false);
    openSettingsView('issues-tasks');
    setTimeout(() => {
      try {
        window.dispatchEvent(new CustomEvent('issuesTasks:reportBug', { detail: { contextUrl: window.location.href } }));
      } catch {}
    }, 50);
  };

  const handleCartChange = (newCartItems: {[assetId: string]: number}) => {
    setCartItems(newCartItems);
    // Fetch full asset details so popup shows correct name/image immediately.
    // Short delay lets the POST /api/cart/add response settle before we re-query.
    setTimeout(() => fetchCartData(), 300);
  };

  const handleOpenCartModal = () => {
    // Fetch fresh cart data when opening modal to ensure synchronization
    fetchCartData();
    setShowCartModal(true);
  };

  const handleCloseCartModal = () => {
    setShowCartModal(false);
  };

  // Helper: determine selected main category (top-level) from a list of category IDs
  const getMainCategoryIdFromSelection = useCallback((ids: string[]): string | null => {
    if (!ids || ids.length === 0) return null;
    // Build a set of main category IDs (no parent)
    const mainIds = new Set<string>(categories.filter(cat => !cat.parentCategory).map(cat => cat._id));
    // If any selected id is a main category, return it directly
    for (const id of ids) {
      if (mainIds.has(id)) return id;
    }
    // Otherwise, climb parent links from the first selected id to find the top-level parent
    const idToParent: Record<string, string | null> = {};
    categories.forEach(cat => {
      let parentId: string | null = null;
      if (typeof (cat as any).parentCategory === 'string') parentId = (cat as any).parentCategory as string;
      else if ((cat as any).parentCategory && typeof (cat as any).parentCategory === 'object') parentId = ((cat as any).parentCategory as any)._id;
      idToParent[cat._id] = parentId;
    });
    const first = ids[0];
    let parent = idToParent[first] || null;
    while (parent && idToParent[parent]) {
      parent = idToParent[parent] as string | null;
    }
    return parent;
  }, [categories]);

  // Lägg till denna funktion i samma område som andra hanterarfunktioner
  const handleCategorySelect = useCallback((categoryIds: string[]) => {
    console.log('Category selection changed:', categoryIds);
    // Uppdatera valda kategori-IDn
    // Reset badge filters if main category changed
    setSelectedFilterCategoryIds(prevIds => {
      const previousMain = getMainCategoryIdFromSelection(prevIds);
      const nextMain = getMainCategoryIdFromSelection(categoryIds);
      if (previousMain !== nextMain) {
        setSelectedFilterBadgeIds([]);
      }
      return categoryIds;
    });
    
    // REMOVED: Don't update searchParams.categories as it conflicts with selectedFilterCategoryIds
    // The fetchAssets function already uses selectedFilterCategoryIds for category filtering
  }, [categories, getMainCategoryIdFromSelection]);

  const handleTabletCategorySelect = useCallback((categoryIds: string[]) => {
    if (!isBrowseView) {
      navigateToBrowseView();
    }
    handleCategorySelect(categoryIds);
    setSelectedAssetId(null);
    setTabletCategoryDrawerOpen(false);
  }, [handleCategorySelect, isBrowseView, navigateToBrowseView]);

  // Function to fetch cart data from API to sync with CartModal
  const fetchCartData = async () => {
    try {
      console.log('Fetching cart data from API...');
      const response = await axiosInstance.get(`/api/cart`);
      const apiCartItems = response.data;
      
      console.log('API cart response:', apiCartItems);
      
      // Convert API response to the format expected by TopAppBar
      const cartItemsForUI: {[assetId: string]: number} = {};
      const assetIds: string[] = [];
      
      if (Array.isArray(apiCartItems)) {
        apiCartItems.forEach((item: any) => {
          if (item.assetId && item.quantity) {
            cartItemsForUI[item.assetId] = item.quantity;
            assetIds.push(item.assetId);
          }
        });
      }
      
      console.log('Converted cart items for UI:', cartItemsForUI);
      setCartItems(cartItemsForUI);
      
      // Fetch asset details for hover display (chunked to avoid URL length limits)
      if (assetIds.length > 0) {
        try {
          const CHUNK_SIZE = 50;
          const allCartAssets: any[] = [];
          for (let i = 0; i < assetIds.length; i += CHUNK_SIZE) {
            const chunk = assetIds.slice(i, i + CHUNK_SIZE);
            const assetsResponse = await axiosInstance.get(`/api/assets`, {
              params: { assetIds: chunk.join(',') }
            });
            if (Array.isArray(assetsResponse.data)) {
              allCartAssets.push(...assetsResponse.data);
            }
          }
          setCartAssets(allCartAssets);
          console.log('Cart assets fetched:', allCartAssets.length);
        } catch (assetError) {
          console.error('Error fetching cart asset details:', assetError);
        }
      } else {
        setCartAssets([]);
      }
    } catch (error) {
      console.error('Error fetching cart data:', error);
      // Don't clear cart items on error, keep existing state
    }
  };

  // Load cart data when user logs in
  useEffect(() => {
    if (currentUser) {
      console.log('User logged in, fetching cart data...');
      // Fetch cart data with a small delay to ensure backend is ready
      setTimeout(() => {
        fetchCartData();
      }, 2000); // ge utrymme för assets/preview att starta först
    } else {
      // Clear cart when user logs out
      setCartItems({});
    }
  }, [currentUser]);

  const openAssetDetailsById = useCallback((assetId: string, options?: { preserveCurrentView?: boolean }) => {
    const normalizedAssetId = String(assetId || '').trim();
    const preserveCurrentView = options?.preserveCurrentView === true;
    if (!normalizedAssetId) return;

    if (getFullPageAssetId()) {
      clearFullPageAssetRoute({ replacePath: '/' });
    }

    if (!preserveCurrentView) {
      try { window.location.hash = ''; } catch {}
      setCurrentSettingsView(null);
      setShowCollectionDetail(false);
      setBrandGuideView(null);
      setShowBriefs(false);
      setShowPrompts(false);
      setShowAiImageStudio(false);
      setShowRecipeCorrection(false);
      setShowInhouse(false);
      setShowListningar(false);
    setShowNyhetslanseringar(false);
      setShowBarcodeGenerator(false);
      setShowTrends(false);
    setShowPresentations(false);
    }

    manualMultiSelectEnabledRef.current = false;
    setMultiSelectEnabled(false);
    setSelectedAssetIds([]);
    setSelectedAssetId(normalizedAssetId);
  }, [clearFullPageAssetRoute]);

  // Lyssna på menyhändelser och hash för att öppna sidor via SettingsPage
  useEffect(() => {
    const openDuplicates = () => {
      setShowPptPolicies(false);
      setCurrentSettingsView('duplicates');
    };
    window.addEventListener('dam:navigate-duplicates', openDuplicates);
    const openArchive = () => {
      setShowPptPolicies(false);
      setCurrentSettingsView('archive');
    };
    window.addEventListener('dam:navigate-archive', openArchive);
    const openAssetDetails = (event: any) => {
      const assetId = String(event?.detail?.assetId || '').trim();
      const preserveCurrentView = event?.detail?.preserveCurrentView === true;
      if (!assetId) return;
      openAssetDetailsById(assetId, { preserveCurrentView });
    };
    window.addEventListener('dam:open-asset-details', openAssetDetails);
    // Styr settings-vy via hash
    const onHashChange = () => {
      if (!currentUser) return;
      const h = (window.location.hash || '').replace('#', '');
      if (h === 'trends') {
        handleOpenTrends();
        return;
      }
      if (
        settingsViewOrder.includes(h as Exclude<SettingsView, null>)
        && canAccessSettingsView(h as Exclude<SettingsView, null>)
      ) {
        setShowPptPolicies(false);
        setCurrentSettingsView(h as Exclude<SettingsView, null>);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    // Kör en initial kontroll när komponenten mountas
    onHashChange();
    return () => {
      window.removeEventListener('dam:navigate-duplicates', openDuplicates);
      window.removeEventListener('dam:navigate-archive', openArchive);
      window.removeEventListener('dam:open-asset-details', openAssetDetails);
      window.removeEventListener('hashchange', onHashChange);
    };
  }, [currentUser, openAssetDetailsById, settingsViewOrder, canAccessSettingsView]);

  useEffect(() => {
    if (!currentUser || deepLinkHandledRef.current) return;

    const assetId = deepLinkedAssetIdRef.current;
    if (!assetId) return;

    deepLinkHandledRef.current = true;
    openAssetDetailsById(assetId);
  }, [currentUser, openAssetDetailsById]);

  // Huvudrenderingslogik
  // Reset-password hanteras nu av AppWrapper
  
  const canEditBrandGuide = hasPermission(currentUser, 'brandGuide:update');
  const canAccessPptPolicies = hasAnyPermission(currentUser, [
    'pptPolicies:view',
    'pptPolicies:update',
    'pptPolicies:publish',
    'pptPolicies:manageFiles',
  ]);
  const canEditPptPolicies = hasAnyPermission(currentUser, [
    'pptPolicies:update',
    'pptPolicies:publish',
    'pptPolicies:manageFiles',
  ]);
  const canManageInventory = hasPermission(currentUser, 'system:manageInventory');
  const canAccessBriefs = hasPermission(currentUser, 'briefs:read');
  const canAccessPrompts = hasPermission(currentUser, 'prompts:use') || hasPermission(currentUser, 'prompts:admin');
  const canAccessAiImageStudio =
    hasPermission(currentUser, 'aiImages:use') ||
    hasPermission(currentUser, 'aiImages:review') ||
    hasPermission(currentUser, 'aiImages:publish') ||
    hasPermission(currentUser, 'aiImages:admin');
  const canAccessRecipeCorrection =
    hasPermission(currentUser, 'recipeCorrection:use') ||
    hasPermission(currentUser, 'recipeCorrection:admin');
  const canAccessInhouse =
    hasPermission(currentUser, 'inhouse:read') ||
    hasPermission(currentUser, 'inhouse:create') ||
    hasPermission(currentUser, 'inhouse:update') ||
    hasPermission(currentUser, 'inhouse:delete');
  const canAccessListningar = hasAnyPermission(currentUser, [
    'listningar:read',
    'listningar:create',
    'listningar:update',
    'listningar:delete',
  ]);
  const canAccessNyhetslanseringar = NYHETSLANSERINGAR_UI_ENABLED && hasPermission(currentUser, 'nyhetslanseringar:read');
  const canAccessBarcodeGenerator = hasPermission(currentUser, 'barcode:access');
  const canAccessTrends = (process.env.REACT_APP_TRENDS_UI !== 'false') && hasPermission(currentUser, 'trends:read');
  const canAccessPresentations = PRESENTATIONS_UI_ENABLED && hasPermission(currentUser, 'presentations:read');
  const canAccessKnowledgeHub = KNOWLEDGE_HUB_UI_ENABLED && hasPermission(currentUser, 'knowledgeHub:read');
  const canAdminKnowledgeHub = hasPermission(currentUser, 'knowledgeHub:admin');
  const isFullPageAssetView = Boolean(fullPageAssetRoute);
  useEffect(() => {
    if (currentSettingsView === 'knowledge-hub' && !canAdminKnowledgeHub) {
      setCurrentSettingsView(null);
    }
  }, [currentSettingsView, canAdminKnowledgeHub]);
  useEffect(() => {
    if (showPptPolicies && !canAccessPptPolicies) {
      setShowPptPolicies(false);
    }
  }, [showPptPolicies, canAccessPptPolicies]);
  useEffect(() => {
    if (showBriefs && !canAccessBriefs) {
      setShowBriefs(false);
    }
  }, [showBriefs, canAccessBriefs]);
  useEffect(() => {
    if (showPrompts && !canAccessPrompts) {
      setShowPrompts(false);
    }
  }, [showPrompts, canAccessPrompts]);
  useEffect(() => {
    if (showAiImageStudio && !canAccessAiImageStudio) {
      setShowAiImageStudio(false);
    }
  }, [showAiImageStudio, canAccessAiImageStudio]);
  useEffect(() => {
    if (showRecipeCorrection && !canAccessRecipeCorrection) {
      setShowRecipeCorrection(false);
    }
  }, [showRecipeCorrection, canAccessRecipeCorrection]);
  useEffect(() => {
    if (showInhouse && !canAccessInhouse) {
      setShowInhouse(false);
    }
  }, [showInhouse, canAccessInhouse]);
  useEffect(() => {
    if (showListningar && !canAccessListningar) {
      setShowListningar(false);
    }
  }, [showListningar, canAccessListningar]);
  useEffect(() => {
    if (showNyhetslanseringar && !canAccessNyhetslanseringar) {
      setShowNyhetslanseringar(false);
    }
  }, [showNyhetslanseringar, canAccessNyhetslanseringar]);
  useEffect(() => {
    if (showBarcodeGenerator && !canAccessBarcodeGenerator) {
      setShowBarcodeGenerator(false);
    }
  }, [showBarcodeGenerator, canAccessBarcodeGenerator]);
  useEffect(() => {
    if (showTrends && !canAccessTrends) {
      setShowTrends(false);
    }
  }, [showTrends, canAccessTrends]);
  useEffect(() => {
    if (showPresentations && !canAccessPresentations) {
      setShowPresentations(false);
    }
  }, [showPresentations, canAccessPresentations]);

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ml: 2}}>Laddar applikation...</Typography>
      </Box>
    );
  }

  if (!currentUser) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }
  
  // Om currentUser finns, rendera resten av appen

  let mainContent;
  if (showRecipeCorrection && canAccessRecipeCorrection && recipeCorrectionAssetId) {
    mainContent = (
      <RecipeCorrectionModule
        assetId={recipeCorrectionAssetId}
        currentUser={currentUser}
        openedFromFullPage={recipeCorrectionOpenedFromFullPage}
        readOnlySnapshot={recipeCorrectionSnapshotMode}
        onClose={handleCloseRecipeCorrection}
        onApplied={handleRecipeCorrectionApplied}
      />
    );
  } else if (fullPageAssetRoute) {
    mainContent = (
      <AssetDetailFullPage
        key={`${fullPageAssetRoute.assetId}-${fullPageRemountKey}`}
        assetId={fullPageAssetRoute.assetId}
        allCategories={categories}
        onAssetDeleted={(deletedAssetId) => {
          handleAssetDeleted(deletedAssetId);
          closeFullPageAsset();
        }}
        onClose={closeFullPageAsset}
        onAssetUpdate={handleAssetUpdate}
        collections={collections}
        templates={templates}
        isAdmin={hasPermission(currentUser, 'admin:all')}
        canUpdate={hasPermission(currentUser, 'assets:update')}
        canDelete={hasPermission(currentUser, 'assets:delete')}
        canManageVersions={hasPermission(currentUser, 'assets:manageVersions')}
        canFavorite={hasPermission(currentUser, 'assets:manageFavorites')}
        canDownload={hasPermission(currentUser, 'assets:download')}
        canValidooSync={hasPermission(currentUser, 'assets:update')}
        canShare={hasPermission(currentUser, 'assets:share')}
        assetIds={fullPageAssetRoute.hasBrowseContext ? assets.map(a => a._id) : []}
        onAssetChange={fullPageAssetRoute.hasBrowseContext ? handleFullPageAssetChange : undefined}
        onOpenAiImageStudioForRecipeAsset={canAccessAiImageStudio ? handleOpenAiImageStudioForRecipeAsset : undefined}
        onOpenAiImageStudioForEditAsset={canAccessAiImageStudio ? handleOpenAiImageStudioForEditAsset : undefined}
        onOpenRecipeCorrection={canAccessRecipeCorrection ? handleOpenRecipeCorrection : undefined}
      />
    );
  } else if (brandGuideView?.type === 'landing') {
    mainContent = (
      <BrandGuideLandingPage
        onClose={handleBackToMainView}
        onOpenSection={handleOpenBrandGuideSection}
        canEdit={canEditBrandGuide}
        onOpenAdmin={
          canEditBrandGuide ? () => handleOpenBrandGuideEditor(null) : undefined
        }
        onOpenPptPolicies={canAccessPptPolicies ? handleOpenPptPolicies : undefined}
      />
    );
  } else if (brandGuideView?.type === 'section') {
    mainContent = (
      <BrandGuideSectionPage
        slug={brandGuideView.slug}
        onBack={() => setBrandGuideView({ type: 'landing' })}
        onNavigate={(newSlug) => setBrandGuideView({ type: 'section', slug: newSlug })}
        canEdit={canEditBrandGuide}
        onEdit={canEditBrandGuide ? (slug) => handleOpenBrandGuideEditor(slug) : undefined}
      />
    );
  } else if (showPptPolicies && canAccessPptPolicies) {
    mainContent = (
      <PptPoliciesPage
        onClose={handleClosePptPolicies}
        canEdit={canEditPptPolicies}
        onOpenAdmin={canEditPptPolicies ? () => openSettingsView('ppt-policies') : undefined}
        onOpenBrandGuide={handleOpenBrandGuideLanding}
      />
    );
  } else if (showBriefs && canAccessBriefs) {
    mainContent = (
      <BriefsModule onClose={handleCloseBriefs} currentUser={currentUser} />
    );
  } else if (showPrompts && canAccessPrompts) {
    mainContent = (
      <PromptsModule onClose={handleClosePrompts} currentUser={currentUser} />
    );
  } else if (showAiImageStudio && canAccessAiImageStudio) {
    mainContent = (
      <AIImageStudioModule
        onClose={handleCloseAiImageStudio}
        currentUser={currentUser}
        initialRecipeAssetId={initialAiRecipeAssetId}
        initialEditAssetId={initialAiEditAssetId}
      />
    );
  } else if (showListningar && canAccessListningar) {
    mainContent = (
      <ListningarModule onClose={handleCloseListningar} currentUser={currentUser} />
    );
  } else if (showNyhetslanseringar && canAccessNyhetslanseringar) {
    mainContent = (
      <NyhetslanseringarModule onClose={handleCloseNyhetslanseringar} currentUser={currentUser} />
    );
  } else if (showInhouse && canAccessInhouse) {
    mainContent = (
      <InhouseModule onClose={handleCloseInhouse} currentUser={currentUser} />
    );
  } else if (showBarcodeGenerator && canAccessBarcodeGenerator) {
    mainContent = (
      <BarcodeModule onClose={handleCloseBarcodeGenerator} currentUser={currentUser} />
    );
  } else if (showTrends && canAccessTrends) {
    mainContent = (
      <TrendsModule currentUser={currentUser} onClose={handleCloseTrends} />
    );
  } else if (showPresentations && canAccessPresentations) {
    mainContent = (
      <PresentationsModule onClose={handleClosePresentations} currentUser={currentUser} />
    );
  } else if (currentSettingsView && canAccessSettingsView(currentSettingsView)) {
    mainContent = (
      <KnowledgeHubAdminAccessProvider value={canAdminKnowledgeHub}>
        <SettingsPage
          currentView={currentSettingsView}
          currentUser={currentUser}
          onClose={handleCloseSettings}
          brandGuideSlugToEdit={brandGuideEditorSlug}
          onBrandGuideSlugHandled={() => setBrandGuideEditorSlug(null)}
        />
      </KnowledgeHubAdminAccessProvider>
    );
  } else {
    // Alltid visa AssetGrid i huvudinnehållet, oavsett om en asset är vald eller inte
    // CHANGED: Removed fixed height calc, using height: 100% to fill available flex space
    mainContent = (
      <Container maxWidth={false} sx={{ py: 0, px: 0, height: '100%' }}>
        <AssetGrid 
          assets={assets}
          totalAssetCount={totalAssetCount}
          hasMoreAssets={hasMoreAssets}
          onLoadMoreAssets={handleLoadMoreAssets}
          onAssetSelect={handleAssetSelect} 
          loading={loadingAssets} 
          error={assetError}
          selectedAssetIds={selectedAssetIds}
          onSelectedAssetsChange={handleSelectedAssetsChange}
            onSelectionAnchorChange={setSelectionAnchorId}
          multiSelectEnabled={multiSelectEnabled}
          thumbnailSize={thumbnailSize}
          allCategories={categories}
          collections={collections}
          onCollectionUpdated={fetchCollections}
          onCartChange={handleCartChange}
          onThumbnailSizeChange={handleThumbnailSizeChange}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          onManageAssetBadges={handleOpenAssetBadgeManager}
          selectedBadgeIds={selectedFilterBadgeIds}
          onBadgeToggle={handleBadgeFilterToggle}
          onClearBadges={handleClearBadgeFilters}
          availableBadges={allBadges}
          badgeFilterMode={badgeFilterMode}
          onBadgeFilterModeToggle={() => setBadgeFilterMode(prev => prev === 'AND' ? 'OR' : 'AND')}
          activeCategoryIds={selectedFilterCategoryIds}
          templates={templates}
          chainFilterKey={chainFilterKey}
          onChainFilterChange={handleChainFilterChange}
          wineFilterKey={wineFilterKey}
          onWineFilterChange={handleWineFilterChange}
          kontorFilter={kontorFilter}
          onKontorFilterChange={handleKontorFilterChange}
          onSortOrderChange={handleAssetGridSortOrderChange}
          popularAiTags={popularAiTags}
          popularAiTagsLoading={popularAiTagsLoading}
          onAiTagToggle={handleAiTagChipToggle}
          selectedAiTags={selectedFilterAiTagNames}
          compactToolbar={showCompactLandscape}
          tabletToolbar={isTabletBrowseMode}
          canUpdate={hasPermission(currentUser, 'assets:update')}
          canManageBadges={hasPermission(currentUser, 'assets:manageBadges')}
          canAddToCollection={hasPermission(currentUser, 'collections:addAssets')}
          canShare={hasPermission(currentUser, 'assets:share')}
          canArchive={hasPermission(currentUser, 'assets:archive')}
          canSaveForValidoo={canUseValidooExport(currentUser)}
          canSortByWordPressPublishedAt={hasPermission(currentUser, 'assets:sortWordPressPublishedAt')}
          canFilterKontorProducts={canFilterKontorProducts}
          canFilterKontorWine={canFilterKontorWine}
          activeCollectionId={selectedCollectionId}
          onRemoveFromCollection={handleRemoveFromCollection}
        />
      </Container>
    ) as any;
  }

  // Bestäm användarroll för onboarding
  const hasRoleName = (user: IUser | null, roleNames: string[]): boolean => {
    if (!user?.roles || roleNames.length === 0) return false;
    const allowed = new Set(roleNames.map(name => name.toLowerCase()));
    return user.roles.some(role => {
      if (typeof role === 'string') {
        return allowed.has(role.toLowerCase());
      }
      return !!role?.name && allowed.has(role.name.toLowerCase());
    });
  };

  const getSupportAudienceForOnboarding = (): 'all' | 'editor' | 'admin' => {
    if (hasPermission(currentUser, 'admin:all')) return 'admin';
    if (
      hasRoleName(currentUser, ['editor', 'redaktör', 'redaktor']) ||
      hasPermission(currentUser, 'downloadTemplates:update')
    ) {
      return 'editor';
    }
    return 'all';
  };

  const getUserRoleForOnboarding = (): 'kontor' | 'byra' | 'admin' => {
    if (hasPermission(currentUser, 'admin:all')) return 'admin';
    if (hasPermission(currentUser, 'assets:create')) return 'byra';
    return 'kontor';
  };

  if (showPresentations && canAccessPresentations) {
    return (
      <UploadManagerProvider>
        <OnboardingProvider enabled={!!currentUser}>
          <Box sx={{ height: '100vh', width: '100%', overflow: 'hidden', bgcolor: '#F4F1EA' }}>
            <CssBaseline />
            <PresentationsModule onClose={handleClosePresentations} currentUser={currentUser} />
          </Box>
        </OnboardingProvider>
      </UploadManagerProvider>
    );
  }

  return (
    <UploadManagerProvider>
    <OnboardingProvider enabled={!!currentUser}>
    <OnboardingWrapper 
      userName={currentUser?.firstName || currentUser?.email?.split('@')[0]}
      userRole={getUserRoleForOnboarding()}
      supportAudience={getSupportAudienceForOnboarding()}
    >
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <CssBaseline />
      <TopAppBar 
        currentUser={currentUser}
        onLogout={handleLogout}
        onUploadClick={handleOpenUploadModal}
        onEnhancedUploadClick={handleOpenEnhancedUploadModal}
        onAddCategoryClick={handleOpenAddCategoryModal}
        onManageCategoriesClick={handleOpenCategoriesSettings}
        onManageTemplatesClick={handleOpenTemplateManagementModal}
        onManageBadgesClick={handleOpenBadgesSettings}
        onRegenerateThumbnailsClick={handleRegenerateThumbnails}
        multiSelectEnabled={multiSelectEnabled}
        selectedAssetCount={selectedAssetIds.length}
        onToggleMultiSelect={handleToggleMultiSelect}
        onOpenRoleSettings={() => openSettingsView('roles')}
        onOpenUserSettings={() => openSettingsView('users')}
        onOpenApiKeySettings={handleOpenApiKeys}
        onOpenBackupSettings={handleOpenBackups}
        onOpenInventorySettings={canManageInventory ? handleOpenInventory : undefined}
        onOpenCsvSettings={handleOpenCsv}
        onOpenFieldDiagnostics={handleOpenFieldDiagnostics}
        onOpenDownloadTemplates={() => openSettingsView('download-templates')}
        onOpenArchive={() => openSettingsView('archive')}
        onOpenDuplicates={() => openSettingsView('duplicates')}
        onOpenIssuesTasks={handleOpenIssuesTasks}
        onReportBug={handleReportBug}
        onOpenPopupSettings={() => setShowPopupSettings(true)}
        onLogoClick={handleLogoClick}
        onOpenSettings={handleOpenSettings}
        onOpenProductComparison={() => openSettingsView('product-comparison')}
        onOpenWordPressPublishing={() => openSettingsView('wordpress-publishing')}
        wordpressPendingAssetCount={wordpressPendingAssetCount}
        onOpenBrandGuide={handleOpenBrandGuideLanding}
        onOpenPptPolicies={canAccessPptPolicies ? handleOpenPptPolicies : undefined}
        onOpenPresentations={canAccessPresentations ? handleOpenPresentations : undefined}
        onOpenTrends={canAccessTrends ? handleOpenTrends : undefined}
        onOpenBriefs={undefined /* hidden while Briefs is on hold */}
        onOpenPrompts={canAccessPrompts ? handleOpenPrompts : undefined}
        onOpenAiImageStudio={canAccessAiImageStudio ? handleOpenAiImageStudio : undefined}
        onOpenListningar={canAccessListningar ? handleOpenListningar : undefined}
        onOpenNyhetslanseringar={canAccessNyhetslanseringar ? handleOpenNyhetslanseringar : undefined}
        onOpenInhouse={canAccessInhouse ? handleOpenInhouse : undefined}
        onOpenBarcodeGenerator={canAccessBarcodeGenerator ? handleOpenBarcodeGenerator : undefined}
        cartItems={cartItems}
        cartAssets={cartAssets}
        onCartClick={handleOpenCartModal}
        onSyncAllDabas={handleSyncAllDabas}
        compactLandscape={showCompactLandscape}
        landscapeSearchTerm={searchParams.searchTerm}
        onLandscapeSearchChange={(value) => {
          setSearchParams(prev => ({ ...prev, searchTerm: value }));
        }}
        onOpenCategoryDrawer={() => setLandscapeCatDrawerOpen(true)}
        onToggleLandscapeCompact={() => setLandscapeCompactDisabled(prev => !prev)}
        tabletShellMode={isTabletShellMode}
        tabletSearchTerm={searchParams.searchTerm}
        onTabletSearchChange={handleTabletSearchChange}
        onOpenTabletCategoryDrawer={() => setTabletCategoryDrawerOpen(true)}
        onOpenTabletFilters={handleOpenTabletFilters}
      />
      <MaintenanceNotice topOffset={showCompactLandscape ? 36 : 64} />
      <KnowledgeHubLauncher canAccess={canAccessKnowledgeHub} showDebugScores={canAdminKnowledgeHub} />

      {/* Category drawer for landscape mobile compact mode */}
      {showCompactLandscape && (
        <MobileNavDrawer
          open={landscapeCatDrawerOpen}
          onClose={() => setLandscapeCatDrawerOpen(false)}
          categories={categories}
          selectedCategoryIds={selectedFilterCategoryIds}
          onCategorySelect={(ids) => {
            setSelectedFilterCategoryIds(ids);
            setLandscapeCatDrawerOpen(false);
          }}
        />
      )}

      {isTabletShellMode && (
        <>
          <TabletCategoryDrawer
            open={tabletCategoryDrawerOpen}
            onClose={() => setTabletCategoryDrawerOpen(false)}
            categories={categories}
            selectedCategoryIds={selectedFilterCategoryIds}
            onCategorySelect={handleTabletCategorySelect}
            currentUser={currentUser}
            onOpenTrends={undefined /* hidden from nav until UX is ready — access via /trends */}
            onOpenBriefs={undefined /* hidden while Briefs is on hold */}
            onOpenBrandGuide={handleOpenBrandGuideLanding}
            onOpenPptPolicies={canAccessPptPolicies ? handleOpenPptPolicies : undefined}
            onOpenPresentations={canAccessPresentations ? handleOpenPresentations : undefined}
            onOpenPrompts={canAccessPrompts ? handleOpenPrompts : undefined}
            onOpenAiImageStudio={canAccessAiImageStudio ? handleOpenAiImageStudio : undefined}
            onOpenNyhetslanseringar={canAccessNyhetslanseringar ? handleOpenNyhetslanseringar : undefined}
            onOpenBarcodeGenerator={canAccessBarcodeGenerator ? handleOpenBarcodeGenerator : undefined}
            onOpenIssuesTasks={undefined /* moved under Inställningar */}
            onOpenSettings={handleOpenSettings}
          />
          <TabletFilterDrawer
            open={tabletFiltersOpen}
            onClose={() => setTabletFiltersOpen(false)}
            initialParams={searchParams}
            recentSearches={recentSearches}
            onSearch={handleAdvancedSearch}
            onResetAll={resetFiltersToAll}
          />
        </>
      )}
      
      {/* Message for thumbnail regeneration */}
      {regenerateMessage && (
        <Alert 
          severity={regenerateMessage.includes('Error') ? 'error' : 'info'}
          sx={{ 
            position: 'fixed', 
            top: '64px', 
            left: '50%', 
            transform: 'translateX(-50%)', 
            zIndex: 9999,
            width: 'auto',
            maxWidth: '80%'
          }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setRegenerateMessage(null)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {regenerateMessage}
          {regeneratingThumbnails && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </Alert>
      )}

      {/* Dabas sync progress dialog */}
      <Dialog 
        open={dabasSyncDialogOpen} 
        onClose={() => !dabasSyncing && setDabasSyncDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2
        }}>
          <Box sx={{ 
            width: 40, 
            height: 40, 
            borderRadius: '50%', 
            backgroundColor: '#ff9800',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <CloudSyncIcon sx={{ color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {dabasSyncing ? 'Synkroniserar med Dabas...' : 'Dabas-synkronisering klar'}
            </Typography>
            {!dabasSyncing && dabasSyncResults && (
              <Typography variant="body2" color="text.secondary">
                {dabasSyncResults.successCount} av {dabasSyncResults.totalAssets} produkter synkroniserades
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {dabasSyncing ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
              <CircularProgress size={48} sx={{ color: '#ff9800' }} />
              <Typography variant="body1" color="text.secondary">
                Hämtar produktdata från Dabas...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Detta kan ta en stund beroende på antalet produkter.
              </Typography>
            </Box>
          ) : dabasSyncResults && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Summary boxes */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Paper sx={{ 
                  flex: 1, 
                  p: 2, 
                  textAlign: 'center',
                  backgroundColor: '#e8f5e9',
                  border: '1px solid #c8e6c9'
                }}>
                  <Typography variant="h4" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                    {dabasSyncResults.successCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Lyckades</Typography>
                </Paper>
                <Paper sx={{ 
                  flex: 1, 
                  p: 2, 
                  textAlign: 'center',
                  backgroundColor: dabasSyncResults.failedCount > 0 ? '#ffebee' : '#f5f5f5',
                  border: dabasSyncResults.failedCount > 0 ? '1px solid #ffcdd2' : '1px solid #e0e0e0'
                }}>
                  <Typography variant="h4" sx={{ color: dabasSyncResults.failedCount > 0 ? '#c62828' : '#757575', fontWeight: 600 }}>
                    {dabasSyncResults.failedCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Misslyckades</Typography>
                </Paper>
              </Box>

              {/* Failed items */}
              {dabasSyncResults.failedCount > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#c62828', fontWeight: 600 }}>
                    Misslyckade produkter
                  </Typography>
                  <Paper variant="outlined" sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {dabasSyncResults.failures.map((f, idx) => (
                      <Box 
                        key={idx} 
                        sx={{ 
                          p: 1.5, 
                          borderBottom: idx < dabasSyncResults.failures.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider',
                          '&:hover': { backgroundColor: '#fafafa' }
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#666' }}>
                            ID: {f.assetId}
                          </Typography>
                          {f.articleNumber && (
                            <Chip label={`Art: ${f.articleNumber}`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                          )}
                        </Box>
                        {f.productName && (
                          <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                            {f.productName}
                          </Typography>
                        )}
                        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                          {f.message}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                </Box>
              )}

              {/* Success items - collapsible */}
              {dabasSyncResults.successCount > 0 && (
                <Box>
                  <Button 
                    size="small" 
                    onClick={() => setShowDabasSuccessList(s => !s)}
                    sx={{ mb: 1, color: '#2e7d32' }}
                  >
                    {showDabasSuccessList ? 'Dölj lyckade produkter' : `Visa lyckade produkter (${dabasSyncResults.successCount})`}
                  </Button>
                  <Collapse in={showDabasSuccessList}>
                    <Paper variant="outlined" sx={{ maxHeight: 200, overflowY: 'auto' }}>
                      {dabasSyncResults.successes.map((s, idx) => (
                        <Box 
                          key={idx} 
                          sx={{ 
                            p: 1.5, 
                            borderBottom: idx < dabasSyncResults.successes.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                            '&:hover': { backgroundColor: '#fafafa' }
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#666' }}>
                              ID: {s.assetId}
                            </Typography>
                            {s.articleNumber && (
                              <Chip label={`Art: ${s.articleNumber}`} size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                            )}
                          </Box>
                          {s.productName && (
                            <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                              {s.productName}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Paper>
                  </Collapse>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          {!dabasSyncing && dabasSyncResults && (
            <Button
              size="small"
              onClick={async () => {
                try {
                  const lines: string[] = [];
                  lines.push(`Dabas-synkronisering: ${dabasSyncResults.successCount} lyckades, ${dabasSyncResults.failedCount} misslyckades`);
                  lines.push('');
                  if (dabasSyncResults.successes.length) {
                    lines.push('=== LYCKADE ===');
                    dabasSyncResults.successes.forEach(s => {
                      lines.push(`OK | ID: ${s.assetId} | Art: ${s.articleNumber || '-'} | ${s.productName || '-'}`);
                    });
                  }
                  if (dabasSyncResults.failures.length) {
                    lines.push('');
                    lines.push('=== MISSLYCKADE ===');
                    dabasSyncResults.failures.forEach(f => {
                      lines.push(`FEL | ID: ${f.assetId} | Art: ${f.articleNumber || '-'} | ${f.productName || '-'} | ${f.message}`);
                    });
                  }
                  await navigator.clipboard.writeText(lines.join('\n'));
                } catch {}
              }}
              startIcon={<ContentCopyIcon />}
            >
              Kopiera logg
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button 
            onClick={() => setDabasSyncDialogOpen(false)} 
            disabled={dabasSyncing}
            variant={dabasSyncing ? 'text' : 'contained'}
            sx={!dabasSyncing ? { backgroundColor: '#ff9800', '&:hover': { backgroundColor: '#f57c00' } } : {}}
          >
            {dabasSyncing ? 'Vänta...' : 'Stäng'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload toast */}
      <Snackbar
        open={uploadToast.open}
        autoHideDuration={3500}
        onClose={() => setUploadToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setUploadToast(prev => ({ ...prev, open: false }))}
          severity={uploadToast.severity}
          sx={{ width: '100%' }}
        >
          {uploadToast.message}
        </Alert>
      </Snackbar>

      <Dialog open={recipeSavedNotice} onClose={() => setRecipeSavedNotice(false)}>
        <DialogTitle>Receptet är sparat.</DialogTitle>
        <DialogContent>
          <Typography>De godkända fälten har skrivits till receptet.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecipeSavedNotice(false)} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>

      {/* Global regenerate-all progress dialog */}
      <Dialog open={regenAllDialogOpen} onClose={() => setRegenAllDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Regenererar thumbnails…</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress variant={regenAllProgress?.total ? 'determinate' : 'indeterminate'} value={regenAllProgress?.total ? (Math.min(regenAllProgress.current, regenAllProgress.total) / regenAllProgress.total) * 100 : 0} />
            </Box>
            <Typography variant="caption" sx={{ minWidth: 80, textAlign: 'right' }}>
              {regenAllProgress ? `${Math.min(regenAllProgress.current, regenAllProgress.total)}/${regenAllProgress.total}` : ''}
            </Typography>
          </Box>
          {regenAllProgress?.fileName && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
              {regenAllProgress.fileName}
            </Typography>
          )}
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
            {regenAllProgress?.etaSeconds != null ? `≈ ${Math.max(0, regenAllProgress.etaSeconds)}s kvar` : ''}
            {regenAllProgress ? ` • OK: ${regenAllProgress.success} • Fel: ${regenAllProgress.errors}` : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenAllDialogOpen(false)}>Dölj</Button>
        </DialogActions>
      </Dialog>
      
      {/* CHANGED: Layout now uses full viewport height with top padding so content sits below the fixed AppBar.
          OLD: sx={{ mt: '64px', mb: 2, pt: 2, px: 2, width: '100%', flexGrow: 1 }}
          This prevents double scrollbars and removes the 64px whitespace at the bottom. */}
      <Box component="main" sx={{ height: '100vh', pt: showCompactLandscape ? '60px' : '72px', px: showCompactLandscape ? 1 : (showPresentations ? 0 : 2), width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        <CssBaseline />

        {/* Top area for search and filtering - only shown in browse view */}
        {isBrowseView && !isFullPageAssetView && !showCompactLandscape && !isTabletShellMode && (
          <Paper sx={{ mb: 1, p: 2, bgcolor: '#f5f5f5', borderRadius: '8px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
              {/* Search Bar - centered and shorter */}
              <Box sx={{ maxWidth: '800px', width: '100%' }}>
                <AdvancedSearchBar 
                  key={`search-reset-${searchResetNonce}`}
                  onSearch={handleAdvancedSearch} 
                  initialParams={searchParams}
                  recentSearches={recentSearches}
                  onResetAll={resetFiltersToAll}
                  onAddCategoryFilter={(categoryId: string) => {
                    if (!selectedFilterCategoryIds.includes(categoryId)) {
                      setSelectedFilterCategoryIds(prev => [...prev, categoryId]);
                    }
                  }}
                  onAddTagFilter={(tag: string, kind: 'tag' | 'aiTag') => {
                    if (kind === 'tag') {
                      if (!selectedFilterTagNames.includes(tag)) {
                        setSelectedFilterTagNames(prev => [...prev, tag]);
                      }
                    } else {
                      if (!selectedFilterAiTagNames.includes(tag)) {
                        setSelectedFilterAiTagNames(prev => [...prev, tag]);
                      }
                    }
                  }}
                />
              </Box>
              
              {/* Category Filter below search */}
              <Box sx={{ width: '100%' }}>
                <CategoryFilter 
                  categories={categories}
                  selectedCategoryIds={selectedFilterCategoryIds}
                  onCategoryToggle={handleCategoryFilterToggle}
                  onCategorySelect={handleCategorySelect}
                />
                {/* Tag filters as chips */}
                {(selectedFilterTagNames.length > 0 || selectedFilterAiTagNames.length > 0) && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {selectedFilterTagNames.map((t) => (
                      <Chip
                        key={`tag-${t}`}
                        label={`Tagg: ${t}`}
                        onDelete={() => {
                          setSelectedFilterTagNames(prev => prev.filter(x => x !== t));
                          if (selectedFilterTagNames.length === 1 && selectedFilterAiTagNames.length === 0 && selectedFilterCategoryIds.length === 0 && selectedFilterBadgeIds.length === 0) {
                            // Sista filtret tas bort – säkerställ tomt fritext:
                            setSearchParams(prev => ({ ...prev, searchTerm: '' }));
                          }
                          // Ingen direkt fetch här – useEffect koordinerar hämtningen
                        }}
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    ))}
                    {selectedFilterAiTagNames.map((t) => (
                      <Chip
                        key={`aitag-${t}`}
                        label={`AI‑tagg: ${t}`}
                        onDelete={() => {
                          setSelectedFilterAiTagNames(prev => prev.filter(x => x !== t));
                          if (selectedFilterAiTagNames.length === 1 && selectedFilterTagNames.length === 0 && selectedFilterCategoryIds.length === 0 && selectedFilterBadgeIds.length === 0) {
                            setSearchParams(prev => ({ ...prev, searchTerm: '' }));
                          }
                          // Ingen direkt fetch här – useEffect koordinerar hämtningen
                        }}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        )}
        
        {/* Main Layout using Flexbox - CHANGED: Added flex: 1 to allow it to fill remaining space */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, width: '100%', flex: 1, overflow: 'hidden' }}>
          
          {/* Collection Sidebar - hide when showing settings 
              CHANGED: Removed fixed height and position sticky - now uses flex layout.
              OLD: height: 'calc(100vh - 64px)', position: 'sticky', top: '64px' */}
          {!currentSettingsView && !isFullPageAssetView && !showPresentations && (
            <Box 
              sx={{ 
                width: { md: collectionSidebarCollapsed ? 50 : 320 }, 
                flexShrink: 0,
                display: { xs: 'none', md: 'block' },
                height: '100%',
                transition: 'width 0.3s ease',
              }}
            >
            <CollectionSidebar
              collections={collections}
              loading={loadingCollections}
              error={collectionError}
              onCollectionSelected={handleCollectionSelected}
              onCreateCollection={() => setIsCollectionManagementModalOpen(true)}
              isAdmin={hasPermission(currentUser, 'admin:all')}
              adminShowAllCollections={adminShowAllCollections}
              onAdminShowAllCollectionsChange={setAdminShowAllCollections}
              onShareCollection={handleShareCollection}
              onDeleteCollection={(collection) => setCollectionToDelete(collection)}
              selectedCollectionId={selectedCollectionId}
              currentUserId={currentUser?._id}
              onDrop={handleDropOnCollection}
              onToggleCollapsed={toggleCollectionSidebarCollapsed}
              isCollapsed={collectionSidebarCollapsed}
              bottomInsetPx={bulkOperationBarInsetPx}
            />
          </Box>
          )}
          
          {/* Main Content Area - now wider when filtering moved up 
              CHANGED: Removed fixed height calc(100vh - 64px), now uses flex: 1 to fill available space.
              OLD: height: 'calc(100vh - 64px)' - this caused the page to extend beyond viewport */}
          <Box 
            ref={scrollContainerRef}
            sx={{ 
              width: { 
                xs: '100%', 
                md: selectedAssetId && !multiSelectEnabled && !isFullPageAssetView ? '65%' : '100%'
              }, 
              pr: { md: selectedAssetId && !multiSelectEnabled && !isFullPageAssetView ? 2 : 0 },
              pb: bulkOperationBarVisible ? `calc(${bulkOperationBarInsetPx}px + 8px)` : 0,
              overflowY: 'auto',
              flex: 1,
              minHeight: 0
            }}
          >
            {selectedCollectionId && !showPresentations && (() => {
              const col = collections.find(c => c._id === selectedCollectionId);
              return col ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, minHeight: 48, position: 'sticky', top: 0, zIndex: 6, bgcolor: '#fff', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle1" sx={{ color: '#072f53', fontSize: '1.1rem', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <strong>Samling:</strong> {col.name}
                  </Typography>
                  <Chip
                    label="Dölj samling"
                    size="small"
                    onDelete={() => handleCollectionSelected(null)}
                    deleteIcon={<CloseIcon />}
                    sx={{ flexShrink: 0 }}
                  />
                </Box>
              ) : null;
            })()}
            {mainContent}
          </Box>

          {/* Right Asset Details Sidebar (desktop/tablet) 
              CHANGED: Removed fixed height calc(100vh - 64px), now uses height: 100% within flex container.
              OLD: height: 'calc(100vh - 64px)' */}
          {!isMobile && !isFullPageAssetView && selectedAssetId && !multiSelectEnabled && (
            <Box sx={{ 
              width: { xs: '100%', md: '35%' }, 
              mt: { xs: 2, md: 0 }, 
              bgcolor: '#f5f5f5', 
              borderRadius: '8px', 
              position: 'relative',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              {/* Stängknapp i övre hörnet */}
              <IconButton 
                onClick={closeAssetDetails} 
                sx={{ 
                  position: 'absolute', 
                  top: 8, 
                  right: 8, 
                  zIndex: 10, 
                  bgcolor: 'white',
                  width: 36,
                  height: 36,
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.1)'
                  }
                }}
                size="medium"
              >
                <Box component="span" sx={{ fontSize: '24px', fontWeight: 'bold' }}>×</Box>
              </IconButton>
              
              {/* CHANGED: Reduced pt from 4 to 1 and removed pb to align with grid window height and minimize whitespace */}
              <Box sx={{ pt: 1, px: 2, height: '100%', overflow: 'hidden' }}>
                <AssetDetails 
                  assetId={selectedAssetId} 
                  allCategories={categories} 
                  onAssetDeleted={handleAssetDeleted}
                  onAssetUpdate={handleAssetUpdate}
                  collections={collections}
                  templates={templates}
                  isAdmin={hasPermission(currentUser, 'admin:all')}
                  canUpdate={hasPermission(currentUser, 'assets:update')}
                  canDelete={hasPermission(currentUser, 'assets:delete')}
                  canManageVersions={hasPermission(currentUser, 'assets:manageVersions')}
                  canFavorite={hasPermission(currentUser, 'assets:manageFavorites')}
                  canDownload={hasPermission(currentUser, 'assets:download')}
                  canValidooSync={hasPermission(currentUser, 'assets:update')}
                  canShare={hasPermission(currentUser, 'assets:share')}
                  onClose={closeAssetDetails}
                  assetIds={assets.map(a => a._id)}
                  onAssetChange={(newId) => setSelectedAssetId(newId)}
                  onOpenFullPage={(assetId) => openFullPageAssetById(assetId, { hasBrowseContext: true })}
                  onOpenAiImageStudioForRecipeAsset={canAccessAiImageStudio ? handleOpenAiImageStudioForRecipeAsset : undefined}
                  onOpenAiImageStudioForEditAsset={canAccessAiImageStudio ? handleOpenAiImageStudioForEditAsset : undefined}
                  onOpenRecipeCorrection={canAccessRecipeCorrection ? handleOpenRecipeCorrection : undefined}
                />
              </Box>
            </Box>
          )}
        </Box> {/* End Main Layout Flexbox */}

        {/* Mobile Asset Details as full-screen dialog */}
        {isMobile && selectedAssetId && !multiSelectEnabled && (
          <Dialog open onClose={closeAssetDetails} fullScreen>
            <DialogContent sx={{ p: 0 }}>
              <Box sx={{ position: 'relative', height: '100%', bgcolor: '#f5f5f5' }}>
                <IconButton 
                  onClick={closeAssetDetails}
                  sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, bgcolor: 'white' }}
                >
                  <Box component="span" sx={{ fontSize: '24px', fontWeight: 'bold' }}>×</Box>
                </IconButton>
                {/* CHANGED: Reduced pt from 6 to 3 and removed pb for mobile to align height and minimize whitespace */}
                <Box sx={{ pt: 3, px: 2, height: '100%', overflow: 'auto' }}>
                  <AssetDetails 
                    assetId={selectedAssetId} 
                    allCategories={categories} 
                    onAssetDeleted={handleAssetDeleted}
                    onAssetUpdate={handleAssetUpdate}
                    collections={collections}
                    templates={templates}
                    isAdmin={hasPermission(currentUser, 'admin:all')}
                    canUpdate={hasPermission(currentUser, 'assets:update')}
                    canDelete={hasPermission(currentUser, 'assets:delete')}
                    canManageVersions={hasPermission(currentUser, 'assets:manageVersions')}
                    canFavorite={hasPermission(currentUser, 'assets:manageFavorites')}
                    canDownload={hasPermission(currentUser, 'assets:download')}
                    canValidooSync={hasPermission(currentUser, 'assets:update')}
                    canShare={hasPermission(currentUser, 'assets:share')}
                    onClose={closeAssetDetails}
                    assetIds={assets.map(a => a._id)}
                    onAssetChange={(newId) => setSelectedAssetId(newId)}
                    onOpenAiImageStudioForRecipeAsset={canAccessAiImageStudio ? handleOpenAiImageStudioForRecipeAsset : undefined}
                    onOpenAiImageStudioForEditAsset={canAccessAiImageStudio ? handleOpenAiImageStudioForEditAsset : undefined}
                    onOpenRecipeCorrection={canAccessRecipeCorrection ? handleOpenRecipeCorrection : undefined}
                  />
                </Box>
              </Box>
            </DialogContent>
          </Dialog>
        )}
        
        {/* Original Operations Bar (auto when 2+ selected or explicit multi-select) */}
        {bulkOperationBarVisible && (
          <BulkOperationBar 
            selectedAssetIds={selectedAssetIds}
            onClearSelection={handleClearSelection}
            categories={categories}
            onOperationComplete={handleBulkOperationComplete}
            onOpenCsvSettings={handleOpenCsv}
            currentUser={currentUser}
            onHeightChange={setBulkOperationBarHeight}
          />
        )}

        {/* Bulk-edit Drawer (skeleton) - always available when event fires */}
        <BulkEditDrawer 
          open={bulkDrawerOpen}
          onClose={() => setBulkDrawerOpen(false)}
          selectedAssetIds={selectedAssetIds}
          categories={categories as any}
          onComplete={() => {
            // After bulk edits, refresh assets so thumbnails and labels reflect changes
            try { fetchAssets(); } catch {}
          }}
        />
        
        {/* Modals */}
        <UploadModal 
          open={isUploadModalOpen} 
          onClose={handleCloseUploadModal} 
          onUploadSuccess={handleAssetUploadSuccess} 
          categories={categories} 
          loadingCategories={loadingCategories} 
        />
        
        <EnhancedUploadModal 
          open={isEnhancedUploadModalOpen} 
          onClose={handleCloseEnhancedUploadModal} 
          onUploadSuccess={handleAssetUploadSuccess} 
          categories={categories}
        />

        <ProductImageValidationRunDialog
          open={uploadValidationDialogOpen}
          title="Bildkontroll Validoo"
          job={uploadValidationJob}
          run={uploadValidationRun}
          error={uploadValidationError}
          starting={uploadValidationStarting}
          loadingRun={uploadValidationLoadingRun}
          onClose={closeUploadValidationDialog}
          onDownload={(format) => void downloadUploadValidationRun(format)}
        />
        
        <AddCategoryModal 
          open={isAddCategoryModalOpen} 
          onClose={handleCloseAddCategoryModal} 
          onCategoryAdded={handleAddCategorySuccess} 
          categories={categories} 
        />
        
        <EditCategoryModal 
          category={categoryToEdit} 
          onClose={handleCloseEditModal}
          onCategoryEdited={() => {
            fetchCategories(); // Refresh categories list
            fetchAssets();    // Refresh assets as categories might affect them
          }}
          allCategories={categories}
        />
        
        <CollectionManagementModal
          open={isCollectionManagementModalOpen}
          onClose={() => setIsCollectionManagementModalOpen(false)}
          collections={collections}
          onCollectionSelected={handleCollectionSelected}
          loading={loadingCollections}
          error={collectionError}
          onCollectionUpdated={fetchCollections}
          createOnly
        />
        
        <TemplateManagementModal
          open={isTemplateManagementModalOpen}
          onClose={handleCloseTemplateManagementModal}
          categories={categories}
          onTemplateAdded={handleTemplateAdded}
          onTemplateUpdated={handleTemplateUpdated}
        />

        <AssetBadgeManager
          open={isAssetBadgeManagerOpen}
          onClose={handleCloseAssetBadgeManager}
          asset={assetForBadgeManagement}
          assetIds={badgeManagerAssetIds || undefined}
          onBadgesUpdated={(updatedAsset: IAsset) => {
            handleBadgeUpdated();
            if (updatedAsset) {
              // Update the asset in the assets list
              setAssets(prevAssets => 
                prevAssets.map(asset => 
                  asset._id === updatedAsset._id ? updatedAsset : asset
                )
              );
            }
          }}
          onBatchBadgesUpdated={() => {
            handleBadgeUpdated();
          }}
        />

        {/* ShareCollectionDialog */}
        <ShareCollectionDialog
          open={shareCollectionDialogOpen}
          onClose={() => setShareCollectionDialogOpen(false)}
          collection={collectionToShare}
          onSettingsSaved={fetchCollections}
        />

        {/* Delete Collection Confirmation */}
        <Dialog
          open={!!collectionToDelete}
          onClose={() => setCollectionToDelete(null)}
          PaperProps={{ sx: { borderRadius: '12px', minWidth: 360, p: 1 } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 0.5 }}>
            <WarningAmberRoundedIcon sx={{ color: '#e65100', fontSize: 28 }} />
            <span>Ta bort samling</span>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Är du säker på att du vill ta bort samlingen <strong>{collectionToDelete?.name}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Tillgångarna i samlingen kommer inte att raderas.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setCollectionToDelete(null)} color="inherit">
              Avbryt
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                if (!collectionToDelete) return;
                axiosInstance.delete(`/api/collections/${collectionToDelete._id}`)
                  .then(() => {
                    fetchCollections();
                    if (selectedCollectionId === collectionToDelete._id) {
                      setSelectedCollectionId(null);
                      setSearchParams(prev => {
                        const newParams = { ...prev };
                        delete newParams.collectionId;
                        return newParams;
                      });
                    }
                  })
                  .catch(err => {
                    console.error('Error deleting collection:', err);
                  })
                  .finally(() => setCollectionToDelete(null));
              }}
            >
              Ta bort
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cart Modal */}
        <CartModal
          open={showCartModal}
          onClose={handleCloseCartModal}
          onCartUpdate={() => {
            // Update cart items in parent when cart changes
            fetchCartData(); // Fetch fresh data from API
          }}
        />

        {/* Popup News Modal */}
        <PopupNewsModal
          open={showPopupNews}
          onClose={() => setShowPopupNews(false)}
        />

        {/* Popup Settings Modal (Admin) */}
        <PopupSettingsModal
          open={showPopupSettings}
          onClose={() => setShowPopupSettings(false)}
        />
      </Box>
    </Box>
    </OnboardingWrapper>
    </OnboardingProvider>
    </UploadManagerProvider>
  );
}

export default AppWrapper;
